import Vue from "vue";
import i18n from "@/i18n";
import store from "@/store";
import iban from "iban";
import { bus } from '@/utils/event-bus';
import { digitalSignatureCall } from '@/utils/digitalSignature';
import { formMapperHandler, filterOutNotUsedFieldsByNationality, nibHolderParticipantMap } from "@/utils/formMapper";
import { particularPersonTabMapping, companyPersonTabMapping } from "@/enums/proposal/proposalFormType";
import {FINANCIAL_IDS} from "@/enums/financials/financialIds";

import { monthsV2, yearsV2 } from "@/utils/simulation/staticDropdownValues";

const initialState = {
  form: {},
  activeTab: {},
  disabledActionButtons: false,
  loading: false,
  bankInfoType: "PT",
  bankTypeOptions: [{
    value: 1,
    text: "Nacional",
    selected: true
  }, {
    value: 2,
    text: "Internacional",
  }],
  participantOptions: [
    {
      value: -1,
      text: i18n.t('dropdownSelection'),
      disabled: true,
    }],
  formNationality: "",
  ibanNationalData: { "value": "" },
  ibanInternationalData: { "value": "" },
  copyAddressObject: null,
  history: []
};

function validateBICC(input) {
  const regex = /^[0-9]{9}[0-9a-zA-Z]{2}[0-9]$/;
  if (input.length !== 12) {
    return false;
  }
  return regex.test(input);
}

function validateBI(nBi, nBiChk) {

	if (nBi=="" || nBiChk=="" || nBiChk.length!=1)
		return false;

	if (nBi=="12345678" && nBiChk=="9")
		return false;

	let totalSum = 0;
	for(let n=0; n<nBi.length; n++) {
		let pos =(nBi.length-n) + 1;
		let value = parseInt(nBi.substring(n,n+1));
		totalSum += value * pos;
	}
	let resto = totalSum % 11;
	let res2  = (resto-11)*(-1);
	let resultadoCalculo = 0;
	if( res2>=10 )
		resultadoCalculo = 0;
	else
		resultadoCalculo = res2;

	if(parseInt(nBiChk) == resultadoCalculo)
		return true;

	return false;
}

function isSpanishNational(nationality) {
  return (
    nationality === "Espanha" || 
    nationality === "ESPANHA" || 
    nationality === "España" || 
    nationality === "ESPAÑA"
  )               
}

function validateDNI(dni, isSpanish) {
  //NIE
  const regExpNIE = /[XYZ]{1}[0-9]{7}[A-Z]{1}/;
  if (regExpNIE.test(dni) && !isSpanish) {
    const firstChar = dni.substring(0, 1);
    switch (firstChar) {
      case 'X':
        dni = dni.replace('X', '0');
        break;
      case 'Y':
        dni = dni.replace('Y', '1');
        break;
      case 'Z':
        dni = dni.replace('Z', '2');
        break;
      default:
        break;
    }
  }

  //DNI
  const regExp = /[0-9]{8}[A-Z]/;
  const digitControl = 'TRWAGMYFPDXBNJZSQVHLCKE';
  const invalid = ['00000000T', '00000001R', '99999999R'];

  return (
      invalid.indexOf(dni) < 0 &&
      regExp.test(dni) &&
      dni.charAt(8) === digitControl.charAt(parseInt(dni.substring(0, 8)) % 23)
  );
}

function concatKeyValues(obj, keyToBeAdded) {
  for (let prop in obj) {
    if (typeof obj[prop] === 'object' && obj[prop] !== null) {
      concatKeyValues(obj[prop], keyToBeAdded);
    } else if (prop === 'key') {
      obj[prop] = obj[prop] + "_" + keyToBeAdded;
    }
  }
  return obj;
}

const state = {
  ...initialState,
};

function manipulateRestOfTabs(tabs, nextTabId, nextTabTitle, neoNextTabId, neoSaveKey, legacySaveKey) {
  const nextTab = tabs.find(
    (tab) => tab.id === nextTabId
  );

  const hasFourthTab = tabs.some(
    (tab) => tab.id === 4.3
  );

  if (hasFourthTab && nextTabId === 4.2) {
    const fourthTab = tabs.find(
      (tab) => tab.id === 4.3
    );
    fourthTab.title = "Titular 3";
    fourthTab.id = 4.2;
    fourthTab.saveKey = fourthTab.saveKey === "fourthClientCompany" ? "thirdClientCompany" : "thirdClientPerson";
  }

  nextTab.title = nextTabTitle;
  nextTab.id = neoNextTabId;
  nextTab.saveKey = nextTab.saveKey === neoSaveKey + "ClientCompany" ? legacySaveKey + "ClientCompany" : legacySaveKey + "ClientPerson";
}

function handleESProposalIdFields(options, state, form, key, value) {
  const selectedNationality = options.find((o) => o.selected);
  const dniField = state.activeTab.form.find((form) => form?.key?.startsWith("personalData"))?.fields.find((field) => field?.key?.startsWith("BI"));
  const dtExpireCCText = state.activeTab.form.find((form) => form?.key?.startsWith("personalData"))?.fields.find((field) => field?.key?.startsWith("dtExpireCCText"));
  const neiField = state.activeTab.form.find((form) => form?.key?.startsWith("personalData"))?.fields.find((field) => field?.key?.startsWith("nrResidenceAuthorization"));
  const dtExpireResidenceAuthorizationText = state.activeTab.form.find((form) => form?.key?.startsWith("personalData"))?.fields.find((field) => field?.key?.startsWith("dtExpireResidenceAuthorizationText"));
  const residencePermitTypeField = state.activeTab.form.find((form) => form?.key?.startsWith("personalData"))?.fields.find((field) => field?.key?.startsWith("residencePermitType"));
  const isSpain = selectedNationality.text.toLowerCase() === "españa";

  neiField.required = !isSpain;
  residencePermitTypeField.required = !isSpain;
  dtExpireResidenceAuthorizationText.required = !isSpain;
  dniField.required = isSpain;
  dtExpireCCText.required = isSpain;

  const documentTypeDropdown = state.activeTab.form.find((form) => form?.key?.startsWith("personalData"))?.fields.find((field) => field?.key?.startsWith("documentType"));

  const { dtEmissionCCText, dtEmissionResidenceAuthorizationText } = getESIDFields(state);

  documentTypeDropdown.options = documentTypeDropdown.options.map(item => ({
    ...item
  }));
  documentTypeDropdown.value = isSpain ? "DNI" : "NIE";
  documentTypeDropdown.keyReRender += 1;
  documentTypeDropdown.editable = !isSpain;

  setFieldsVisibilityAndRequirements({ dniField, dtExpireCCText, dtEmissionCCText }, isSpain);
  setFieldsVisibilityAndRequirements({ neiField, dtExpireResidenceAuthorizationText, dtEmissionResidenceAuthorizationText }, !isSpain);

  const field = form.fields.find((f) => f?.key === key);
  if (field?.type === "dropdown") {
    field.value = value?.text;
  }
}

function setFieldsVisibilityAndRequirements(fields, show) {
  Object.values(fields).forEach(field => {
    if (field) {
      field.fieldShow = show;
      field.required = show;
    }
  });
}

function companyPartnerField(cleanParticipantTabForm) {
  const partnerParticipantFields = {
    "label": "Participação",
    "type": "text",
    "key": "quota",
    "saveKey": "quota",
    "inputType": "number",
    "stepNumber": "any",
    "maxNumber": 999,
    "inputIcon": "percent",
    "value": "",
    "editable": true,
    "required": true,
    "flexdir": "col",
    "colspan": 2,
    "fieldShow": false,
    "proposalType": [
      "PT"
    ]
  };
  cleanParticipantTabForm?.form[0]?.fields.splice(1, 0, partnerParticipantFields);
}

function handlePTProposalIdFields(options, state, form, key, value) {
  const selectedNationality = options.find((o) => o.selected);
  const documentTypeDropdown = state.activeTab.form.find((form) => form?.key?.startsWith("personalData"))?.fields.find((field) => field?.key?.startsWith("documentType"));
  const isPortugal = selectedNationality.text.toLowerCase() === "portugal";

  const { neiField, dtExpireResidenceAuthorizationText, dniField, dtExpireCCText, emissionEntityCC, numberPassport, dtExpirePassport } = getPTIDFields(state);

  const proposalFinId = store.getters["ProposalsModule/getProposalFinId"];
  console.log('proposalFinId:: ', proposalFinId)
  //Se a nacionalidade escolhida é Portugal - preselecionar o tipo de documento como BI/Cartão de Cidadão
  //Caso não seja mostrar as restantes opções

  // alterado em 19/07/2023 para 
  //SIFA-2021 Nacionalidade diferente de PT, também pode ter o tipo de documento: BI/Cartão de Cidadão
  documentTypeDropdown.options = documentTypeDropdown.options.map(item => ({
    ...item
  }));

  let optionToShow = proposalFinId == FINANCIAL_IDS.COFIDIS_PT ? "Cartão de Cidadão" : "BI/Cartão de Cidadão";
  documentTypeDropdown.value = isPortugal ? optionToShow : "Passaporte";
  documentTypeDropdown.keyReRender += 1;
  documentTypeDropdown.editable = !isPortugal;

  // Titulo de residencia
  setFieldsVisibilityAndRequirements({ neiField, dtExpireResidenceAuthorizationText }, false);
  // BI/CC
  setFieldsVisibilityAndRequirements({ dniField, dtExpireCCText, emissionEntityCC }, isPortugal);
  // Passaporte
  setFieldsVisibilityAndRequirements({ numberPassport, dtExpirePassport }, !isPortugal);

  const field = form.fields.find((f) => f?.key === key);
  if (field?.type === "dropdown") {
    field.value = value?.text;
  }
}

const hasInvalidValue = (v) => {
  if (v.key === "carYear" && v.value === 0 || v.key === "carMonth" && v.value === 0) {
    v.value = null;
  }
  if (v.key === "documentType" && v.options.find((o) => o.selected) === undefined) {
    v.value = v.value || null;
  }
  return v.value == null || v.value === "";
};

function getPTIDFields(state) {
  // * campos de BI/Cartão de Cidadão
  const nifField = state.activeTab.form.find((form) => form?.key?.startsWith("personalData"))?.fields.find((field) => field?.key?.startsWith("NIF"));
  const dniField = state.activeTab.form.find((form) => form?.key?.startsWith("personalData"))?.fields.find((field) => field?.key?.startsWith("BI"));
  const emissionEntityCC = state.activeTab.form.find((form) => form?.key?.startsWith("personalData"))?.fields.find((field) => field?.key?.startsWith("emissionEntityCC"));
  const dtExpireCCText = state.activeTab.form.find((form) => form?.key?.startsWith("personalData"))?.fields.find((field) => field?.key?.startsWith("dtExpireCCText"));
  const niss = state.activeTab.form.find((form) => form?.key?.startsWith("personalData"))?.fields.find((field) => field?.key?.startsWith("NISS"));

  // * campos de Passaporte
  const numberPassport = state.activeTab.form.find((form) => form?.key?.startsWith("personalData"))?.fields.find((field) => field?.key?.startsWith("nrPassport"));
  const dtExpirePassport = state.activeTab.form.find((form) => form?.key?.startsWith("personalData"))?.fields.find((field) => field?.key?.startsWith("dtExpirePassportText"));

  // * campos de Titulo de residência
  const neiField = state.activeTab.form.find((form) => form?.key?.startsWith("personalData"))?.fields.find((field) => field?.key?.startsWith("nrResidenceAuthorization"));
  const dtExpireResidenceAuthorizationText = state.activeTab.form.find((form) => form?.key?.startsWith("personalData"))?.fields.find((field) => field?.key?.startsWith("dtExpireResidenceAuthorizationText"));
  return { neiField, dtExpireResidenceAuthorizationText, nifField, dniField, dtExpireCCText, niss, numberPassport, dtExpirePassport, emissionEntityCC };
}

function getESIDFields(state) {
  // * campos de DNI
  const dniField = state.activeTab.form.find((form) => form?.key?.startsWith("personalData"))?.fields.find((field) => field?.key?.startsWith("BI"));
  const dtExpireCCText = state.activeTab.form.find((form) => form?.key?.startsWith("personalData"))?.fields.find((field) => field?.key?.startsWith("dtExpireCCText"));
  const dtEmissionCCText = state.activeTab.form.find((form) => form?.key?.startsWith("personalData"))?.fields.find((field) => field?.key?.startsWith("dtEmissionCCText"));

  // * campos de NIE
  const neiField = state.activeTab.form.find((form) => form?.key?.startsWith("personalData"))?.fields.find((field) => field?.key?.startsWith("nrResidenceAuthorization"));
  const dtExpireResidenceAuthorizationText = state.activeTab.form.find((form) => form?.key?.startsWith("personalData"))?.fields.find((field) => field?.key?.startsWith("dtExpireResidenceAuthorizationText"));
  const dtEmissionResidenceAuthorizationText = state.activeTab.form.find((form) => form?.key?.startsWith("personalData"))?.fields.find((field) => field?.key?.startsWith("dtEmissionResidenceAuthorizationText"));
  
  return { neiField, dtExpireResidenceAuthorizationText, dniField, dtExpireCCText, dtEmissionCCText, dtEmissionResidenceAuthorizationText };
}

function addVariations(keys) {
  const newKeys = [];
  for (const key of keys) {
    for (let i = 1; i <= 5; i++) {
      const newKey = `${key}_${i}`;
      if (!keys.includes(newKey)) {
        newKeys.push(newKey);
      }
    }
  }
  return [...keys, ...newKeys];
}

function throwLabelVal(keysToIterate, field, keysToIterateLabels, outOfContextErrors, hasBeenActivatedPhoneVal) {
  for (let i = 0; i < keysToIterate.length; i++) {
    const keyIterated = keysToIterate[i];
    if (field.key.startsWith(keyIterated)) {
      const participantTabs = state.form.tabs.filter((tab) => tab?.dynamicallyCreated || tab?.id === 4);
      if (participantTabs.length > 1) {
        participantTabs.map((tab) => {
          tab?.form.map((formFields) => {
            const fieldFromOtherSpace = formFields.fields.find((fieldFromOtherTab) => fieldFromOtherTab?.key !== field.key && fieldFromOtherTab?.key.startsWith(keyIterated));
            if (fieldFromOtherSpace?.value === field.value && field.key !== fieldFromOtherSpace?.key && fieldFromOtherSpace?.value && field.value) {
              const errorMessage = i18n.t(keysToIterateLabels[keysToIterate[i]]);
              if (outOfContextErrors.get(i18n.t(tab.title)) !== undefined){
                outOfContextErrors.get(i18n.t(tab.title)).push(errorMessage);
                field.hasError = true;
                fieldFromOtherSpace.hasError = true;
                hasBeenActivatedPhoneVal = true;
              }
            }
          });
        });
      }
    }
  }
  return hasBeenActivatedPhoneVal;
}

function sofincoProfessionalCategoryValidation(form, key, state, value) {
  const field = form.fields.find((f) => f.key === key);
  const professionalCategory = state.activeTab.form.find((form) => form.key?.startsWith("professionalStatus"))?.fields.find((field) => field?.key?.startsWith("professionalCategory"));
  const companyActivity = state.activeTab.form.find((form) => form.key?.startsWith("professionalStatus"))?.fields.find((field) => field?.key?.startsWith("professionalStatus"));
  const nrSalaryPayments = state.activeTab.form.find((form) => form?.key?.startsWith("profit"))?.fields.find((field) => field?.key?.startsWith("nrSalaryPayments"));
  const netReceiptValueAvg = state.activeTab.form.find((form) => form?.key?.startsWith("profit"))?.fields.find((field) => field?.key?.startsWith("netReceiptValueAvg"));

  companyActivity.required = value.text !== "Estudiante";
  companyActivity.hasError = false;

  // Fields required consoante a profissão
  const requiredFieldsKeys = ["job", "lastJob", "companyName", "companyPhone", "companyActivity", "professionalStatus", "DTHiredText", "DTHiredEndText",
    "companyPostalCode1", "companyPostalCode2", "companyCity", "companyNIF", "companyCAE", "principalActivityCountry"];
  // const onlyRequiredFieldKeys = ["job", "companyName", "companyPhone", "companyActivity", "professionalStatus", "DTHiredText", "companyCAE", "principalActivityCountry"];
  const onlyRequiredFieldKeys = ["job", "lastJob", "companyName", "companyPhone", "companyActivity", "professionalStatus", "DTHiredText", "DTHiredEndText",
    "companyPostalCode1", "companyPostalCode2", "companyCity", "companyNIF", "companyCAE", "principalActivityCountry"];

  const allRequiredFieldKeys = addVariations(requiredFieldsKeys);
  const allOnlyRequiredFieldKeys = addVariations(onlyRequiredFieldKeys);

  // Todas as opções para bloquear
  const excludedOccupations = ["Ama de casa", "Desempleado con prestación", "Desempleado sin prestación", "Jubilado", "Pre-Jubilado", "Pensionista Viudedad", "Pensionista Incapacidad", "Pensionista Orfandad", "Otras pensiones", "Estudiante"];
  // As restantes opções
  const includedOccupations = professionalCategory?.options?.filter(obj => !excludedOccupations.includes(obj.text)).map(obj => obj.text);

  state.activeTab.form.find((form) => form?.key?.startsWith("professionalStatus"))?.fields.map((field) => {
    if (allRequiredFieldKeys.includes(field.key)) {
      if (allOnlyRequiredFieldKeys.includes(field.key)) {
        field.required = includedOccupations.includes(value?.text);
      }
      field.editable = includedOccupations.includes(value?.text);
      field.hasError = false;
      field.value = includedOccupations.includes(value?.text) ? field.value : "";
      const isDropdownField = field.type === "dropdown";
      const isInvalidValue = excludedOccupations.includes(value?.text);

      if (isDropdownField && isInvalidValue) {
        field?.options?.forEach((option) => {
          option.selected = option?.value === -1;
        });
      }
    }
    return field;
  });

  nrSalaryPayments.required = !excludedOccupations.includes(value?.text);
  netReceiptValueAvg.required = !excludedOccupations.includes(value?.text);

  // https://dtogsc.atlassian.net/browse/SIFA-1413
  // Ama de casa - nºpagas 0
  // Se a categoria profissional for ama de casa, então o número de pagas deve ser zero.
  if (value?.text === "Ama de casa") {
    nrSalaryPayments.options?.forEach((option) => {
      option.selected = 0;
    });
    nrSalaryPayments.value = 0;
    nrSalaryPayments.keyReRender += 1;
    nrSalaryPayments.editable = false;
  } else {
    nrSalaryPayments.editable = true;
  }

  field.value = value?.text;
}

function bbvaEsProfessionalCategoryValidation(form, key, state, value) {
  const field = form.fields.find((f) => f.key === key);
  const professionalCategory = state.activeTab.form.find((form) => form.key?.startsWith("professionalStatus"))?.fields.find((field) => field?.key?.startsWith("professionalCategory"));
  const companyActivity = state.activeTab.form.find((form) => form.key?.startsWith("professionalStatus"))?.fields.find((field) => field?.key?.startsWith("professionalStatus"));
  const nrSalaryPayments = state.activeTab.form.find((form) => form?.key?.startsWith("profit"))?.fields.find((field) => field?.key?.startsWith("nrSalaryPayments"));
  const netReceiptValueAvg = state.activeTab.form.find((form) => form?.key?.startsWith("profit"))?.fields.find((field) => field?.key?.startsWith("netReceiptValueAvg"));

  companyActivity.required = value.text !== "Estudiante";
  companyActivity.hasError = false;

  // Fields required consoante a profissão
  const requiredFieldsKeys = ["job", "lastJob", "companyName", "companyPhone", "companyActivity", "professionalStatus", "DTHiredText", "DTHiredEndText",
    "companyPostalCode1", "companyPostalCode2", "companyCity", "companyNIF", "companyCAE", "principalActivityCountry"];
  // const onlyRequiredFieldKeys = ["job", "companyName", "companyPhone", "companyActivity", "professionalStatus", "DTHiredText", "companyCAE", "principalActivityCountry"];
  const onlyRequiredFieldKeys = ["job", "lastJob", "companyName", "companyPhone", "companyActivity", "professionalStatus", "DTHiredText", "DTHiredEndText",
    "companyPostalCode1", "companyPostalCode2", "companyCity", "companyNIF", "companyCAE", "principalActivityCountry"];

  const allRequiredFieldKeys = addVariations(requiredFieldsKeys);
  const allOnlyRequiredFieldKeys = addVariations(onlyRequiredFieldKeys);

  // Todas as opções para bloquear
  const excludedOccupations = ["Parado", "Desempleado", "Pensionista/Jubilado", "Otros", "Rentista", "Estudiante", "Renta viudedad", "Ama de casa"];
  // As restantes opções
  const includedOccupations = professionalCategory?.options?.filter(obj => !excludedOccupations.includes(obj.text)).map(obj => obj.text);

  const excludedSectors = ["Ama de casa", "Desempleado", "Estudiante", "Pensionista/Jubilado", "Renta de viudedad", "Rentista", "Otros"];
  const companyActivityField = state.activeTab.form.find((form) => form.key?.startsWith("professionalStatus"))?.fields.find((field) => field?.key?.startsWith("companyActivity"));
  companyActivityField.options = store.getters["ProposalsModule/getCompanySector"](companyActivityField.key);
  if (!excludedSectors.includes(professionalCategory.value)) {
    companyActivityField.options = store.getters["ProposalsModule/getCompanySector"](companyActivityField.key).filter(option => !excludedSectors.includes(option.text));
    
    companyActivityField.value = '-1';
  }

  state.activeTab.form.find((form) => form?.key?.startsWith("professionalStatus"))?.fields.map((field) => {
    if (allRequiredFieldKeys.includes(field.key)) {
      if (allOnlyRequiredFieldKeys.includes(field.key)) {
        field.required = includedOccupations.includes(value?.text);
      }
      field.editable = includedOccupations.includes(value?.text);
      field.hasError = false;
      field.value = includedOccupations.includes(value?.text) ? field.value : "";
      const isDropdownField = field.type === "dropdown";
      const isInvalidValue = excludedOccupations.includes(value?.text);

      if (isDropdownField && isInvalidValue) {
        field?.options?.forEach((option) => {
          option.selected = option?.value === -1;
        });
      }
    }
    return field;
  });

  nrSalaryPayments.required = !excludedOccupations.includes(value?.text);
  netReceiptValueAvg.required = !excludedOccupations.includes(value?.text);

  if(field) {
    field.value = value?.text;
  }

}

function caixabankProfessionalCategoryValidation(form, key, state, value) {
  const field = form.fields.find((f) => f.key === key);
  const professionalCategory = state.activeTab.form.find((form) => form.key?.startsWith("professionalStatus"))?.fields.find((field) => field?.key?.startsWith("professionalCategory"));
  const companyActivity = state.activeTab.form.find((form) => form.key?.startsWith("professionalStatus"))?.fields.find((field) => field?.key?.startsWith("professionalStatus"));
  const job = state.activeTab.form.find((form) => form.key?.startsWith("professionalStatus"))?.fields.find((field) => field?.key?.startsWith("job"));
  const nrSalaryPayments = state.activeTab.form.find((form) => form?.key?.startsWith("profit"))?.fields.find((field) => field?.key?.startsWith("nrSalaryPayments"));
  const netReceiptValueAvg = state.activeTab.form.find((form) => form?.key?.startsWith("profit"))?.fields.find((field) => field?.key?.startsWith("netReceiptValueAvg"));
  const dtHiredEnd = state.activeTab.form.find((form) => form.key?.startsWith("professionalStatus"))?.fields.find((field) => field?.key?.startsWith("DTHiredEndText"));
  const dtHired = state.activeTab.form.find((form) => form.key?.startsWith("professionalStatus"))?.fields.find((field) => field?.key?.startsWith("DTHiredText"));

  companyActivity.required = value.text !== "Estudiante";
  companyActivity.hasError = false;

  // Fields required consoante a profissão
  const requiredFieldsKeys = ["job", "lastJob", "companyName", "companyPhone", "companyActivity", "professionalStatus", "DTHiredText", "DTHiredEndText",
    "companyPostalCode1", "companyPostalCode2", "companyCity", "companyNIF", "companyCAE", "principalActivityCountry"];
  // const onlyRequiredFieldKeys = ["job", "companyName", "companyPhone", "companyActivity", "professionalStatus", "DTHiredText", "companyCAE", "principalActivityCountry"];
  const onlyRequiredFieldKeys = ["job", "lastJob", "companyName", "companyPhone", "companyActivity", "professionalStatus", "DTHiredText", "DTHiredEndText",
    "companyPostalCode1", "companyPostalCode2", "companyCity", "companyNIF", "companyCAE", "principalActivityCountry"];

  const allRequiredFieldKeys = addVariations(requiredFieldsKeys);
  const allOnlyRequiredFieldKeys = addVariations(onlyRequiredFieldKeys);

  // Todas as opções para bloquear
  const excludedOccupations = ["Pensionista (jubilación)", "Pensionista (viudedad)", "Pensionista (larga enfermedad)", "Pensionista (orfandad)"];
  const semiExcludedOccupations = ["Desempleado", "Otros", "Pensionista (enfermedad temp.)"];//"Fijo Discontinuo", 
  const allExcludedOccupations = excludedOccupations.concat(semiExcludedOccupations);
  // As restantes opções
  const includedOccupations = professionalCategory?.options?.filter(obj => !allExcludedOccupations.includes(obj.text)).map(obj => obj.text);

  state.activeTab.form.find((form) => form?.key?.startsWith("professionalStatus"))?.fields.map((field) => {
    if (allRequiredFieldKeys.includes(field.key)) {
      if (allOnlyRequiredFieldKeys.includes(field.key)) {
        field.required = includedOccupations.includes(value?.text);
      }
      field.editable = includedOccupations.includes(value?.text);
      field.hasError = false;
      field.value = includedOccupations.includes(value?.text) ? field.value : "";
      const isDropdownField = field.type === "dropdown";
      const isInvalidValue = allExcludedOccupations.includes(value?.text);

      if (isDropdownField && isInvalidValue) {
        field?.options?.forEach((option) => {
          option.selected = option?.value === -1;
        });
      }
    }

    dtHiredEnd.required = value?.text === "Asalariado temporal";
    return field;
  });

  if (semiExcludedOccupations.includes(value?.text)) {
    companyActivity.required = true;
    companyActivity.editable = true;
    companyActivity.hasError = false;
    companyActivity.value = "";
    companyActivity.options?.forEach((option) => {
      option.selected = option?.value === -1;
    });

    job.required = true;
    job.editable = true;
    job.hasError = false;
    job.value = "";
    job.options?.forEach((option) => {
      option.selected = option?.value === -1;
    });
  }

  if (value?.text === "Fijo Discontinuo") {
    dtHired.required = true;
    dtHired.editable = true;
    dtHired.hasError = false;
  }

  nrSalaryPayments.required = !excludedOccupations.includes(value?.text);
  netReceiptValueAvg.required = !excludedOccupations.includes(value?.text);

  if (field !== undefined) {
    field.value = value?.text;
  }
}

function cetelemSpainProfessionalCategoryValidation(form, key, state, value) {
  const field = form.fields.find((f) => f.key === key);
  const professionalCategory = state.activeTab.form.find((form) => form.key?.startsWith("professionalStatus"))?.fields.find((field) => field?.key?.startsWith("professionalCategory"));
  const companyActivity = state.activeTab.form.find((form) => form.key?.startsWith("professionalStatus"))?.fields.find((field) => field?.key?.startsWith("companyActivity"));
  const job = state.activeTab.form.find((form) => form.key?.startsWith("professionalStatus"))?.fields.find((field) => field?.key?.startsWith("job"));
  const nrSalaryPayments = state.activeTab.form.find((form) => form?.key?.startsWith("profit"))?.fields.find((field) => field?.key?.startsWith("nrSalaryPayments"));
  const netReceiptValueAvg = state.activeTab.form.find((form) => form?.key?.startsWith("profit"))?.fields.find((field) => field?.key?.startsWith("netReceiptValueAvg"));
  const idClienttype = state.activeTab?.form?.find((form) => form?.key?.startsWith('generalData'))?.fields?.find((field) => field?.key?.startsWith('idClienttype'));
  const participantType = idClienttype.value;
  const dtHired = state.activeTab.form.find((form) => form.key?.startsWith("professionalStatus"))?.fields.find((field) => field?.key?.startsWith("DTHiredText"));
  const dtHiredEnd = state.activeTab.form.find((form) => form.key?.startsWith("professionalStatus"))?.fields.find((field) => field?.key?.startsWith("DTHiredEndText"));

  companyActivity.required = value.text !== "Estudiante";
  companyActivity.hasError = false;

  // Fields required consoante a profissão ["companyPostalCode1", "companyCity", ]
  const requiredFieldsKeys = ["job", "lastJob", "companyName", "companyPhone", "companyActivity", "professionalStatus", "DTHiredText", "DTHiredEndText",
    "companyPostalCode2", "companyPostalCode1", "companyCity", "companyNIF", "companyCAE", "principalActivityCountry"];
  // const onlyRequiredFieldKeys = ["job", "companyName", "companyPhone", "companyActivity", "professionalStatus", "DTHiredText", "companyCAE", "principalActivityCountry"];
  const onlyRequiredFieldKeys = ["job", "lastJob", "companyName", "companyPhone", "companyActivity", "professionalStatus", "DTHiredText", "DTHiredEndText",
    "companyPostalCode2", "companyNIF", "companyCAE", "principalActivityCountry"];

  const allRequiredFieldKeys = addVariations(requiredFieldsKeys);
  const allOnlyRequiredFieldKeys = addVariations(onlyRequiredFieldKeys);

  // Todas as opções para bloquear
  const excludedOccupations = ["Estudiante", "Jubilado", "No empleado. Demanda empleo", "Otros pasivos"];
  // As restantes opções
  const includedOccupations = professionalCategory?.options?.filter(obj => !excludedOccupations.includes(obj.text)).map(obj => obj.text);
  state.activeTab.form.find((form) => form?.key?.startsWith("professionalStatus"))?.fields.map((field) => {
    if (allRequiredFieldKeys.includes(field.key)) {
      if (allOnlyRequiredFieldKeys.includes(field.key)) {
        field.required = includedOccupations.includes(value?.text);
      }
      field.editable = includedOccupations.includes(value?.text);
      field.hasError = false;
      field.value = includedOccupations.includes(value?.text) ? field.value : "";
      const isDropdownField = field.type === "dropdown";
      const isInvalidValue = excludedOccupations.includes(value?.text);

      if (isDropdownField && isInvalidValue) {
        field?.options?.forEach((option) => {
          option.selected = option?.value === -1;
        });
      }
    }

    if (field.key?.startsWith("DTHiredEndText")) {
      dtHiredEnd.required = includedOccupations.includes(value?.text);
    }
    return field;
  });

  //Sempre que se trate de uma função activa, ou seja, uma das seguintes categorias profissionais: 
  if(field?.key?.startsWith('professionalCategory')) {
    const activeRoles = ["Trabajador por cuenta propia", "Rentista", "Trabajador por cuenta ajena"];
    const categoryProfessional = professionalCategory?.value;
    companyActivity?.options?.map((option) => {
      if(option.value != '-1') {
        option.disabled = false;
      }
      
      if(option.text == 'Estatales: Funcionarios' && activeRoles.includes(categoryProfessional)) {
        option.disabled = true;
      }

      //Se a categoria profissional for: Funcionario
      if(option.text != 'Estatales: Funcionarios' && categoryProfessional == 'Funcionario') {
        option.disabled = true;
      }
    });

    job?.options?.map((option) => {
      if(option.value != '-1') {
        option.disabled = false;
      }

      //Se a categoria profissional for: Funcionario
      if(participantType == 4 && categoryProfessional == 'Rentista' && option.text != 'Rentista') {
        option.disabled = true;
      }
    });

  }

  dtHired.required = true;
  dtHired.editable = true;
  nrSalaryPayments.required = !excludedOccupations.includes(value?.text);
  netReceiptValueAvg.required = !excludedOccupations.includes(value?.text);

  if (field !== undefined) {
    field.value = value?.text;
  }
}

function sabadellProfessionalCategoryValidation(form, key, state, value) {
  const field = form.fields.find((f) => f.key === key);
  const professionalCategory = state.activeTab.form.find((form) => form.key?.startsWith("professionalStatus"))?.fields.find((field) => field?.key?.startsWith("professionalCategory"));
  const professionalStatus = state.activeTab.form.find((form) => form.key?.startsWith("professionalStatus"))?.fields.find((field) => field?.key?.startsWith("professionalStatus"));
  const companyActivity = state.activeTab.form.find((form) => form.key?.startsWith("professionalStatus"))?.fields.find((field) => field?.key?.startsWith("companyActivity"));
  const job = state.activeTab.form.find((form) => form.key?.startsWith("professionalStatus"))?.fields.find((field) => field?.key?.startsWith("job"));
  const nrSalaryPayments = state.activeTab.form.find((form) => form?.key?.startsWith("profit"))?.fields.find((field) => field?.key?.startsWith("nrSalaryPayments"));
  const netReceiptValueAvg = state.activeTab.form.find((form) => form?.key?.startsWith("profit"))?.fields.find((field) => field?.key?.startsWith("netReceiptValueAvg"));
  const dtHiredEnd = state.activeTab.form.find((form) => form.key?.startsWith("professionalStatus"))?.fields.find((field) => field?.key?.startsWith("DTHiredEndText"));
  const dtHired = state.activeTab.form.find((form) => form.key?.startsWith("professionalStatus"))?.fields.find((field) => field?.key?.startsWith("DTHiredText"));

  // Fields required consoante a profissão
  const requiredFieldsKeys = ["job", "lastJob", "companyName", "companyPhone", "companyActivity", "professionalStatus", "DTHiredText", "DTHiredEndText",
    "companyPostalCode1", "companyPostalCode2", "companyCity", "companyNIF", "companyCAE", "principalActivityCountry"];
  const onlyRequiredFieldKeys = ["job", "lastJob", "companyName", "companyPhone", "companyActivity", "professionalStatus", "DTHiredText", "DTHiredEndText",
    "companyPostalCode1", "companyPostalCode2", "companyCity", "companyNIF", "companyCAE", "principalActivityCountry"];

  const allRequiredFieldKeys = addVariations(requiredFieldsKeys);
  const allOnlyRequiredFieldKeys = addVariations(onlyRequiredFieldKeys);

  // Todas as opções para bloquear
  const allExcludedOccupations = ["Parado", "Jubilado", "Pensionista", "No Trabaja"];
  // As restantes opções
  const includedOccupations = professionalCategory?.options?.filter(obj => !allExcludedOccupations.includes(obj.text)).map(obj => obj.text);

  professionalStatus.required = false;
  professionalStatus.editable = false;
  professionalStatus.hasError = false;
  professionalStatus.value = "";
  professionalStatus.fieldShow = false;

  state.activeTab.form.find((form) => form?.key?.startsWith("professionalStatus"))?.fields.map((field) => {
    if (allRequiredFieldKeys.includes(field.key)) {
      if (allOnlyRequiredFieldKeys.includes(field.key)) {
        field.required = includedOccupations.includes(value?.text);
      }
      field.editable = includedOccupations.includes(value?.text);
      field.hasError = false;
      field.value = includedOccupations.includes(value?.text) ? field.value : "";
      const isDropdownField = field.type === "dropdown";
      const isInvalidValue = allExcludedOccupations.includes(value?.text);

      if (isDropdownField && isInvalidValue) {
        field?.options?.forEach((option) => {
          option.selected = option?.value === -1;
        });
      }
    }

    dtHiredEnd.required = value?.text === "Asalariado Temporal" || value?.text === "Temporero";

    return field;
  });

  if (professionalCategory.value === "Parado" || professionalCategory.value === "Jubilado" || professionalCategory.value === "Pensionista") {
    let options = [
      {
        value: 0,
        text: i18n.t("dropdownSelection"),
        disabled: true,
      },
      {
        value: 1,
        text: professionalCategory.value,
      },
    ];
    companyActivity.options = options;
    companyActivity.editable = true;
    companyActivity.required = true;
    job.options = options;
    job.editable = true;
    job.required = true;
  } else if (professionalCategory.value === "No Trabaja") {
    let jobOptions = [
      {
        value: 0,
        text: i18n.t("dropdownSelection"),
        disabled: true,
      },
      {
        value: 1,
        text: "Amas De Casa",
      },
      {
        value: 2,
        text: "Estudiante",
      },
      {
        value: 3,
        text: "Rentista",
      },
    ];
    let companyActivityOptions = [
      {
        value: 0,
        text: i18n.t("dropdownSelection"),
        disabled: true,
      },
      {
        value: 1,
        text: "No Trabaja-Amadecasa Estu",
      },
    ];
    companyActivity.options = companyActivityOptions;
    companyActivity.editable = true;
    companyActivity.required = true;
    job.options = jobOptions;
    job.editable = true;
    job.required = true;
  } else {
    let excludedJobs = ["Amas De Casa", "Estudiante", "Jubilado", "Parado", "Pensionista", "Rentista"];
    let excludedSectors = ["Jubilado", "No Trabaja-Amadecasa Estu", "Parado", "Pensionista"];
    job.options = store.getters["ProposalsModule/getJobs"](job.key).filter(option => !excludedJobs.includes(option.text));
    job.value = i18n.t("dropdownSelection");
    companyActivity.options = store.getters["ProposalsModule/getCompanySector"](companyActivity.key).filter(option => !excludedSectors.includes(option.text));
    companyActivity.value = i18n.t("dropdownSelection");
  }

  if (value?.text === "Fijo Discontinuo") {
    dtHired.required = true;
    dtHired.editable = true;
    dtHired.hasError = false;
  }

  nrSalaryPayments.required = !allExcludedOccupations.includes(value?.text);
  netReceiptValueAvg.required = !allExcludedOccupations.includes(value?.text);

  if (field !== undefined) {
    field.value = value?.text;
  }
}

function sabadellCompanySectorValidation(form, key, state, value) {
  const field = form.fields.find((f) => f.key === key);
  const companyActivity = state.activeTab.form.find((form) => form.key?.startsWith("professionalStatus"))?.fields.find((field) => field?.key?.startsWith("companyActivity"));
  const job = state.activeTab.form.find((form) => form.key?.startsWith("professionalStatus"))?.fields.find((field) => field?.key?.startsWith("job"));

  switch (job.value) {
    case "Diplomático":
      companyActivity.value = "Diplomatica";
      companyActivity.editable = false;
      break;
    case "Religioso":
      companyActivity.value = "Otras Act. Asociativas";
      companyActivity.editable = false;
      break
    case "Artista":
    case "Deportista Profesional":
    case "Escritor":
      companyActivity.value = "Act. Recre. Y Entretenimi";
      companyActivity.editable = false;
      break;
    default:
      companyActivity.editable = true;
      break;
  }

  if (field !== undefined) {
    field.value = value?.text;
  }
}

function santanderEsProfessionalCategoryValidation(form, key, state, value) {
  const field = form.fields.find((f) => f.key === key);
 
  const professionalCategory = state.activeTab.form.find((form) => form.key?.startsWith("professionalStatus"))?.fields.find((field) => field?.key?.startsWith("professionalCategory"));
  const companyActivity = state.activeTab.form.find((form) => form.key?.startsWith("professionalStatus"))?.fields.find((field) => field?.key?.startsWith("professionalStatus"));
  const nrSalaryPayments = state.activeTab.form.find((form) => form?.key?.startsWith("profit"))?.fields.find((field) => field?.key?.startsWith("nrSalaryPayments"));
  const netReceiptValueAvg = state.activeTab.form.find((form) => form?.key?.startsWith("profit"))?.fields.find((field) => field?.key?.startsWith("netReceiptValueAvg"));
  const dtHiredEnd = state.activeTab.form.find((form) => form.key?.startsWith("professionalStatus"))?.fields.find((field) => field?.key?.startsWith("DTHiredEndText"));
  
  companyActivity.required = value.text !== "Estudiante";
  companyActivity.hasError = false;

  // Fields required consoante a profissão
  const requiredFieldsKeys = ["job", "lastJob", "companyName", "companyPhone", "companyActivity", "professionalStatus", "DTHiredText", "DTHiredEndText",
    "companyPostalCode1", "companyPostalCode2", "companyCity", "companyNIF", "companyCAE", "principalActivityCountry"];
 
  const onlyRequiredFieldKeys = ["job", "lastJob", "companyName", "companyPhone", "companyActivity", "professionalStatus", "DTHiredText", "DTHiredEndText",
    "companyPostalCode1", "companyPostalCode2", "companyCity", "companyNIF", "companyCAE", "principalActivityCountry"];

  const allRequiredFieldKeys = addVariations(requiredFieldsKeys);
  const allOnlyRequiredFieldKeys = addVariations(onlyRequiredFieldKeys);

  // Todas as opções para bloquear
  const excludedOccupations = ["Parado", "Pensionista/Jubilado", "Rentista", "Estudiante", "Otros"];
  // As restantes opções
  const includedOccupations = professionalCategory?.options?.filter(obj => !excludedOccupations.includes(obj.text)).map(obj => obj.text);

  state.activeTab.form.find((form) => form?.key?.startsWith("professionalStatus"))?.fields.map((field) => {
    if (allRequiredFieldKeys.includes(field.key)) {
      if (allOnlyRequiredFieldKeys.includes(field.key)) {
        field.required = includedOccupations.includes(value?.text);
      }
      field.editable = includedOccupations.includes(value?.text);
      field.hasError = false;
      field.value = includedOccupations.includes(value?.text) ? field.value : "";
      const isDropdownField = field.type === "dropdown";
      const isInvalidValue = excludedOccupations.includes(value?.text);

      if (isDropdownField && isInvalidValue) {
        field?.options?.forEach((option) => {
          option.selected = option?.value === -1;
        });
      }
    }

    dtHiredEnd.required = value?.text === "Asalariado Temporero" || value?.text === "Temporal";
    return field;
  });

  nrSalaryPayments.required = !excludedOccupations.includes(value?.text);
  netReceiptValueAvg.required = !excludedOccupations.includes(value?.text);

  if(field !== undefined) {
    field.value = value?.text;
  }
}

/* remover depois
function ptProfessionalCategoryValidationV2(form, key, state, value, proposalFinId) {
  const field = form.fields.find((f) => f.key === key);
  const professionalCategory = state.activeTab.form.find((form) => form.key?.startsWith("professionalStatus"))?.fields.find((field) => field?.key?.startsWith("professionalCategory"));
  const companyActivity = state.activeTab.form.find((form) => form.key?.startsWith("professionalStatus"))?.fields.find((field) => field?.key?.startsWith("professionalStatus"));
  const netReceiptValueAvg = state.activeTab.form.find((form) => form?.key?.startsWith("profit"))?.fields.find((field) => field?.key?.startsWith("netReceiptValueAvg"));
  const isSickLeave = state.activeTab.form.find((form) => form?.key?.startsWith("profit"))?.fields.find((field) => field?.key?.startsWith("isSickLeave"));
  const sickLeaveReason = state.activeTab.form.find((form) => form?.key?.startsWith("profit")).fields?.find((fieldWithKey) => fieldWithKey?.key?.startsWith("sickLeaveReason"));

  companyActivity.required = value.text !== "Estudante";
  companyActivity.hasError = false;

  // Fields required consoante a profissão
  const requiredFieldsKeys = ["job", "companyName", "companyPhone", "companyActivity", "professionalStatus", "DTHiredText", "DTHiredEndText",
    "companyPostalCode1", "companyPostalCode2", "companyCity", "companyNIF", "companyCAE", "principalActivityCountry"];
  // const onlyRequiredFieldKeys = ["job", "companyName", "companyPhone", "companyActivity", "professionalStatus", "DTHiredText", "companyCAE", "principalActivityCountry"];
  const onlyRequiredFieldKeys = ["job", "companyName", "companyPhone", "companyActivity", "professionalStatus", "DTHiredText",
    "companyPostalCode1", "companyPostalCode2", "companyCity", "companyNIF", "companyCAE", "principalActivityCountry"];

  const allRequiredFieldKeys = addVariations(requiredFieldsKeys);
  const allOnlyRequiredFieldKeys = addVariations(onlyRequiredFieldKeys);

  // Todas as opções para bloquear
  const excludedOccupations = ["Desempregado", "Doméstica", "Domestica", "Estudante", "Reformado", "Pessoa que vive dos rendimentos"];
  // As restantes opções
  const includedOccupations = professionalCategory?.options?.filter(obj => !excludedOccupations.includes(obj.text)).map(obj => obj.text);
  // Opções para desbloquear última profissão SIFA-1349
  const lastjobException = ["Desempregado", "Reformado", "Pessoa que vive dos rendimentos"];
  // Opções para desbloquear última profissão SIFA-1349
  const dTHiredEndException = ["Prazo", "Temporário"];
  // https://dtogsc.atlassian.net/browse/SIFA-1889
  // Cetelem - Desbloquear o campo Empresa quando a categoria profissional é:005 Outros/Desemp/Reform/Estudante
  const cetelemExceptions = ["Outros", "Desempregado", "Estudante", "Reformado"];

  state.activeTab.form.find((form) => form?.key?.startsWith("professionalStatus"))?.fields.map((field) => {
    if (allRequiredFieldKeys.includes(field.key)) {
      if (allOnlyRequiredFieldKeys.includes(field.key)) {
        field.required = includedOccupations.includes(value?.text);
      }
      field.editable = includedOccupations.includes(value?.text);
      field.hasError = false;
      field.value = includedOccupations.includes(value?.text) ? field.value : "";
      const isDropdownField = field.type === "dropdown";
      const isInvalidValue = excludedOccupations.includes(value?.text);

      if (isDropdownField && isInvalidValue) {
        field?.options?.forEach((option) => {
          option.selected = option?.value === -1;
        });
      }
    }
    // Opções para desbloquear última profissão SIFA-1349
    else if (field.key?.startsWith("lastJob")) {
      field.editable = lastjobException.includes(value?.text);
      field.required = lastjobException.includes(value?.text);
      field.hasError = false;
      field.value = lastjobException.includes(value?.text) ? field.value : "";

      const isDropdownField = field.type === "dropdown";
      const isInvalidValue = lastjobException.includes(value?.text);

      if (isDropdownField && !isInvalidValue) {
        field?.options?.forEach((option) => {
          option.selected = option?.value === -1;
        });
      }
    }
    // Data Fim Actividade - Obrigatoriedade do Campo
    // https://dtogsc.atlassian.net/browse/SIFA-1337
    else if (field.key?.startsWith("DTHiredEndText")) {
      field.required = dTHiredEndException.includes(value?.text);
    }
    return field;
  });

  netReceiptValueAvg.required = !excludedOccupations.includes(value?.text);
  isSickLeave.required = !excludedOccupations.includes(value?.text);
  isSickLeave.editable = !excludedOccupations.includes(value?.text);

  if (excludedOccupations.includes(value?.text)) {
    isSickLeave.value = 2;
    sickLeaveReason.value = "";
    sickLeaveReason.fieldShow = false;
    isSickLeave.keyReRender++;
    sickLeaveReason.keyReRender++;
  }

  const companyName = state.activeTab.form.find((form) => form.key?.startsWith("professionalStatus"))?.fields.find((field) => field?.key?.startsWith("companyName"));
  // https://dtogsc.atlassian.net/browse/SIFA-1889
  // Cetelem - Desbloquear o campo Empresa quando a categoria profissional é:005 Outros/Desemp/Reform/Estudante
  if (cetelemExceptions.includes(value?.text) && companyName && proposalFinId === 8) {
    companyName.editable = true;
  }

  field.value = value?.text;
}
*/

function ptProfessionalCategoryValidation(form, key, state, value, proposalFinId) {
  const excludedOccupations = ["Desempregado", "Doméstica", "Domestica", "Estudante", "Reformado", "Pessoa que vive dos rendimentos"];
  const lastjobException = ["Desempregado", "Reformado", "Pessoa que vive dos rendimentos"];
  const dTHiredEndException = ["Prazo", "Temporário"];
  const cetelemExceptions = ["Outros", "Desempregado", "Estudante", "Reformado"];

  basePtProfessionalCategoryValidation(form, key, state, value, {
    excludedOccupations,
    lastjobException,
    dTHiredEndException,
    getProfessionalStatusRequirement: (value) => value.text !== "Estudante",
    postValidationHook: ({ state, value }) => {
      const companyName = state.activeTab.form.find((f) => f.key?.startsWith("professionalStatus"))?.fields.find((f) => f?.key?.startsWith("companyName"));

      if (cetelemExceptions.includes(value?.text) && companyName && proposalFinId === 8) {
        companyName.editable = true;
      }
    },
  });
}

/*
function bbvaPtProfessionalCategoryValidation(form, key, state, value) {
  const field = form.fields.find((f) => f.key === key);
  if(field === undefined)
    return;
  const professionalCategory = state.activeTab.form.find((form) => form.key?.startsWith("professionalStatus"))?.fields.find((field) => field?.key?.startsWith("professionalCategory"));
  const professionalStatus = state.activeTab.form.find((form) => form.key?.startsWith("professionalStatus"))?.fields.find((field) => field?.key?.startsWith("professionalStatus"));
  const netReceiptValueAvg = state.activeTab.form.find((form) => form?.key?.startsWith("profit"))?.fields.find((field) => field?.key?.startsWith("netReceiptValueAvg"));
  const isSickLeave = state.activeTab.form.find((form) => form?.key?.startsWith("profit"))?.fields.find((field) => field?.key?.startsWith("isSickLeave"));
  const sickLeaveReason = state.activeTab.form.find((form) => form?.key?.startsWith("profit")).fields?.find((fieldWithKey) => fieldWithKey?.key?.startsWith("sickLeaveReason"));

  professionalStatus.required = value.text !== "Estudante";
  professionalStatus.hasError = false;

  // Fields required consoante a profissão
  const requiredFieldsKeys = ["job", "companyName", "companyPhone", "companyActivity", "professionalStatus", "DTHiredText", "DTHiredEndText",
    "companyPostalCode1", "companyPostalCode2", "companyCity", "companyNIF", "companyCAE", "principalActivityCountry"];
  // const onlyRequiredFieldKeys = ["job", "companyName", "companyPhone", "companyActivity", "professionalStatus", "DTHiredText", "companyCAE", "principalActivityCountry"];
  const onlyRequiredFieldKeys = ["job", "companyName", "companyPhone", "companyActivity", "professionalStatus", "DTHiredText",
    "companyPostalCode1", "companyPostalCode2", "companyCity", "companyNIF", "companyCAE", "principalActivityCountry"];

  const allRequiredFieldKeys = addVariations(requiredFieldsKeys);
  const allOnlyRequiredFieldKeys = addVariations(onlyRequiredFieldKeys);

  // Todas as opções para bloquear
  const excludedOccupations = ["Desempregado", "Pensionista/Reformado", "Estudante"];
  // As restantes opções
  const includedOccupations = professionalCategory?.options?.filter(obj => !excludedOccupations.includes(obj.text)).map(obj => obj.text);
  // Opções para desbloquear última profissão SIFA-1349
  const lastjobException = ["Desempregado", "Pensionista/Reformado"];
  // Opções para desbloquear última profissão SIFA-1349
  const dTHiredEndException = ["Temporario ou a prazo"];

  state.activeTab.form.find((form) => form?.key?.startsWith("professionalStatus"))?.fields.map((field) => {
    if (allRequiredFieldKeys.includes(field.key)) {
      if (allOnlyRequiredFieldKeys.includes(field.key)) {
        field.required = includedOccupations.includes(value?.text);
      }
      field.editable = includedOccupations.includes(value?.text);
      field.hasError = false;
      field.value = includedOccupations.includes(value?.text) ? field.value : "";
      const isDropdownField = field.type === "dropdown";
      const isInvalidValue = excludedOccupations.includes(value?.text);

      if (isDropdownField && isInvalidValue) {
        console.log(field)
        if (field.key.startsWith("job")) {
          field.value = "Sem atividade profissional";
        } else if (field.key.startsWith("professionalStatus")) {
          field.value = "Não Aplicável";
        } else {
          field?.options?.forEach((option) => {
            option.selected = option?.value === -1;
          });
        }
      }
    }
    // Opções para desbloquear última profissão SIFA-1349
    else if (field.key?.startsWith("lastJob")) {
      field.editable = lastjobException.includes(value?.text);
      field.required = lastjobException.includes(value?.text);
      field.hasError = false;
      field.value = lastjobException.includes(value?.text) ? field.value : "";

      const isDropdownField = field.type === "dropdown";
      const isInvalidValue = lastjobException.includes(value?.text);

      if (isDropdownField && !isInvalidValue) {
        field?.options?.forEach((option) => {
          option.selected = option?.value === -1;
        });
      }
    }
        // Data Fim Actividade - Obrigatoriedade do Campo
    // https://dtogsc.atlassian.net/browse/SIFA-1337
    else if (field.key?.startsWith("DTHiredEndText")) {
      field.required = dTHiredEndException.includes(value?.text);
    }
    return field;
  });

  netReceiptValueAvg.required = !excludedOccupations.includes(value?.text);
  isSickLeave.required = !excludedOccupations.includes(value?.text);
  isSickLeave.editable = !excludedOccupations.includes(value?.text);

  if (excludedOccupations.includes(value?.text)) {
    isSickLeave.value = 2;
    sickLeaveReason.value = "";
    sickLeaveReason.fieldShow = false;
    isSickLeave.keyReRender++;
    sickLeaveReason.keyReRender++;
  }

  field.value = value?.text;
}
*/

function bbvaPtProfessionalCategoryValidation(form, key, state, value) {
  const excludedOccupations = ["Desempregado", "Pensionista/Reformado", "Estudante"];
  const lastjobException = ["Desempregado", "Pensionista/Reformado"];
  const dTHiredEndException = ["Temporario ou a prazo"];

  basePtProfessionalCategoryValidation(form, key, state, value, {
    excludedOccupations,
    lastjobException,
    dTHiredEndException,
    getProfessionalStatusRequirement: (value) => value.text !== "Estudante",
    onExcludedOccupationDropdownOverride: (field) => {
      if (field.key.startsWith("job")) {
        field.value = "Sem atividade profissional";
        return true;
      } else if (field.key.startsWith("professionalStatus")) {
        field.value = "Não Aplicável";
        return true;
      }
      return false;
    },
  });
}

function cofidisPtProfessionalCategoryValidation(form, key, state, value) {
  const excludedOccupations = ["Diversos"];
  const lastjobException = ["Diversos", "Publico", "Privado", "Trabalhador por Conta Própria"];
  const dTHiredEndException = ["Contrato a Prazo de Termo Certo"];

  basePtProfessionalCategoryValidation(form, key, state, value, {
    excludedOccupations,
    lastjobException,
    dTHiredEndException,
    getProfessionalStatusRequirement: (value) => value.text !== "Diversos",
    onExcludedOccupationDropdownOverride: (field) => {
      if (field.key.startsWith("job")) {
        field.value = "Sem atividade profissional";
        return true;
      } else if (field.key.startsWith("professionalStatus")) {
        field.value = "Não Aplicável";
        return true;
      }
      return false;
    },
    postValidationHook: () => {
      // lastJob editable override specific to Cofidis
      const lastJobField = state.activeTab.form.find((f) => f?.key?.startsWith("professionalStatus"))?.fields.find((f) => f?.key?.startsWith("lastJob"));
      if (lastJobField) {
        lastJobField.editable = true;
        lastJobField.required = false;
      }
    },
  });
}

//handles all shared logic (field lookups, required/editable toggling, sick leave reset, DTHiredEnd, lastJob)
// - getProfessionalStatusRequirement — configurable callback since each variant has different trigger text ("Estudante" vs "Diversos")
// - onExcludedOccupationDropdownOverride — callback used by bbvaPt and cofidis to set custom values on job/professionalStatus fields instead of resetting to -1
// - postValidationHook — for caller-specific logic (e.g. Cetelem company name unlock, Cofidis lastJob.editable = true override)
function basePtProfessionalCategoryValidation(form, key, state, value, {
  excludedOccupations,
  lastjobException,
  dTHiredEndException,
  getProfessionalStatusRequirement,
  onExcludedOccupationDropdownOverride,
  postValidationHook,
}) {
  const field = form.fields.find((f) => f.key === key);
  if (field === undefined) return;

  const professionalCategory = state.activeTab.form.find(
    (f) => f.key?.startsWith("professionalStatus"))?.fields.find((f) => f?.key?.startsWith("professionalCategory"));

  const professionalStatus = state.activeTab.form.find(
    (f) => f.key?.startsWith("professionalStatus"))?.fields.find((f) => f?.key?.startsWith("professionalStatus"));

  const netReceiptValueAvg = state.activeTab.form.find((f) => f?.key?.startsWith("profit"))?.fields.find((f) => f?.key?.startsWith("netReceiptValueAvg"));

  const isSickLeave = state.activeTab.form.find((f) => f?.key?.startsWith("profit"))?.fields.find((f) => f?.key?.startsWith("isSickLeave"));

  const sickLeaveReason = state.activeTab.form.find((f) => f?.key?.startsWith("profit"))?.fields?.find((f) => f?.key?.startsWith("sickLeaveReason"));

  professionalStatus.required = getProfessionalStatusRequirement(value);
  professionalStatus.hasError = false;

  const requiredFieldsKeys = [
    "job", "companyName", "companyPhone", "companyActivity", "professionalStatus",
    "DTHiredText", "DTHiredEndText", "companyPostalCode1", "companyPostalCode2",
    "companyCity", "companyNIF", "companyCAE", "principalActivityCountry",
  ];
  const onlyRequiredFieldKeys = [
    "job", "companyName", "companyPhone", "companyActivity", "professionalStatus",
    "DTHiredText", "companyPostalCode1", "companyPostalCode2",
    "companyCity", "companyNIF", "companyCAE", "principalActivityCountry",
  ];

  const allRequiredFieldKeys = addVariations(requiredFieldsKeys);
  const allOnlyRequiredFieldKeys = addVariations(onlyRequiredFieldKeys);

  const includedOccupations = professionalCategory?.options?.filter((obj) => !excludedOccupations.includes(obj.text)).map((obj) => obj.text);

  state.activeTab.form.find((f) => f?.key?.startsWith("professionalStatus"))?.fields.map((field) => {
      if (allRequiredFieldKeys.includes(field.key)) {
        const isIncluded = includedOccupations.includes(value?.text);
        if (allOnlyRequiredFieldKeys.includes(field.key)) {
          field.required = isIncluded;
        }
        field.editable = isIncluded;
        field.hasError = false;
        field.value = isIncluded ? field.value : "";

        const isDropdownField = field.type === "dropdown";
        const isExcluded = excludedOccupations.includes(value?.text);

        if (isDropdownField && isExcluded) {
          if (onExcludedOccupationDropdownOverride?.(field)) {
            // override handled by caller
          } else {
            field?.options?.forEach((option) => {
              option.selected = option?.value === -1;
            });
          }
        }
      } else if (field.key?.startsWith("lastJob")) {
        field.editable = lastjobException.includes(value?.text);
        field.required = lastjobException.includes(value?.text);
        field.hasError = false;
        field.value = lastjobException.includes(value?.text) ? field.value : "";

        const isDropdownField = field.type === "dropdown";
        if (isDropdownField && !lastjobException.includes(value?.text)) {
          field?.options?.forEach((option) => {
            option.selected = option?.value === -1;
          });
        }
      } else if (field.key?.startsWith("DTHiredEndText")) {
        field.required = dTHiredEndException.includes(value?.text);
      }

      return field;
    });

  netReceiptValueAvg.required = !excludedOccupations.includes(value?.text);
  isSickLeave.required = !excludedOccupations.includes(value?.text);
  isSickLeave.editable = !excludedOccupations.includes(value?.text);

  if (excludedOccupations.includes(value?.text)) {
    isSickLeave.value = 2;
    sickLeaveReason.value = "";
    sickLeaveReason.fieldShow = false;
    isSickLeave.keyReRender++;
    sickLeaveReason.keyReRender++;
  }

  postValidationHook?.({ field, state, value });

  field.value = value?.text;
}

function ptMaritalStateValidation(form, key, state, value) {
  const field = form.fields.find((f) => f.key === key);
  const maritalState = state.activeTab.form.find((form) => form.key?.startsWith("personalInfo"))?.fields.find((field) => field?.key?.startsWith("maritalState"));

  // Fields required consoante a profissão
  const requiredFieldsKeys = ["marriageType"];
  const onlyRequiredFieldKeys = ["marriageType"];

  const allRequiredFieldKeys = addVariations(requiredFieldsKeys);
  const allOnlyRequiredFieldKeys = addVariations(onlyRequiredFieldKeys);

  // Todas as opções para não bloquear
  const includedOptions = ["Casado", "CASADO(A)"];

  // As restantes opções
  const excludedOptions = maritalState?.options?.filter(obj => !includedOptions.includes(obj.text)).map(obj => obj.text);

  state.activeTab.form.find((form) => form?.key?.startsWith("personalInfo"))?.fields.map((field) => {
    if (allRequiredFieldKeys.includes(field.key)) {
      if (allOnlyRequiredFieldKeys.includes(field.key)) {
        field.required = includedOptions.includes(value?.text);
      }
      field.editable = includedOptions.includes(value?.text);
      field.hasError = false;
      field.value = includedOptions.includes(value?.text) ? field.value : "";
      const isDropdownField = field.type === "dropdown";
      const isInvalidValue = excludedOptions.includes(value?.text);

      if (isDropdownField && isInvalidValue) {
        field?.options?.forEach((option) => {
          option.selected = option?.value === -1;
        });
      }
    }
    return field;
  });

  if(field) {
    field.value = value?.text || '';
  }
}

function ptContractTypeValidation(form, key, state, value) {
  const field = form.fields.find((f) => f.key === key);
  if (field === undefined)
    return;
  // Fields required consoante a profissão
  const requiredFieldsKeys = ["DTHiredEndText"];
  const onlyRequiredFieldKeys = ["DTHiredEndText"];

  const allRequiredFieldKeys = addVariations(requiredFieldsKeys);
  const allOnlyRequiredFieldKeys = addVariations(onlyRequiredFieldKeys);

  // Todas as opções para não bloquear
  const includedOptions = ["Prazo", "Temporário", "Temporario ou a prazo", "Contrato a Prazo de Termo Certo"];

  state.activeTab.form.find((form) => form?.key?.startsWith("professionalStatus"))?.fields.map((field) => {
    if (allRequiredFieldKeys.includes(field.key)) {
      if (allOnlyRequiredFieldKeys.includes(field.key)) {
        field.required = includedOptions.includes(value?.text);
      }
      field.hasError = false;
      field.value = includedOptions.includes(value?.text) ? field.value : "";
    }
    return field;
  });
  field.value = value?.text;
}

function cofidisJobValidation(form, key, state, value) {
  const field = form.fields.find((f) => f.key === key);
  if (field === undefined)
    return;
  
  // Fields required consoante a profissão
  const notRequiredFieldsKeys = [
    "companyName", "companyPhone", "companyActivity", "professionalStatus",
    "DTHiredText", "DTHiredEndText", "companyPostalCode1", "companyPostalCode2",
    "companyCity", "companyNIF", "companyCAE", "principalActivityCountry",
  ];

  const allNotRequiredFieldKeys = addVariations(notRequiredFieldsKeys);

  // Todas as opções para bloquear
  const includedOptions = ["Reformados",];

  state.activeTab.form.find((form) => form?.key?.startsWith("professionalStatus"))?.fields.map((field) => {
    if (allNotRequiredFieldKeys.includes(field.key)) {
      field.required = !includedOptions.includes(value?.text);
      field.editable = !includedOptions.includes(value?.text);
      field.hasError = false;
      field.value = !includedOptions.includes(value?.text) ? field.value : "";

      const isDropdownField = field.type === "dropdown";
      const isInvalidValue = includedOptions.includes(value?.text);

      if (isDropdownField && isInvalidValue) {
        field?.options?.forEach((option) => {
          option.selected = option?.value === -1;
        });
      }
    }
    return field;
  });
  field.value = value?.text;
}

function esContractTypeValidation(form, key, state, value) {
  const field = form.fields.find((f) => f.key === key);
  // Fields required consoante a profissão
  const requiredFieldsKeys = ["DTHiredEndText"];
  const onlyRequiredFieldKeys = ["DTHiredEndText"];

  const allRequiredFieldKeys = addVariations(requiredFieldsKeys);
  const allOnlyRequiredFieldKeys = addVariations(onlyRequiredFieldKeys);

  // Todas as opções para não bloquear
  const includedOptions = ["Eventual", "Fin de Obra"];

  state.activeTab.form.find((form) => form?.key?.startsWith("professionalStatus"))?.fields.map((field) => {
    //Fin de Obra
    /*if (field.key?.startsWith("DTHiredText")) {
      field.required = 'Fin de Obra' != value?.text;
    }*/

    if (allRequiredFieldKeys.includes(field.key)) {
      if (allOnlyRequiredFieldKeys.includes(field.key)) {
        field.required = includedOptions.includes(value?.text);
      }
      field.hasError = false;
      field.value = includedOptions.includes(value?.text) ? field.value : "";
    }
    return field;
  });

  if(field) {
    field.value = value?.text;
  }
}

function testIfItIsNotEmail(clientEmail) {
  //return !/^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/.test(clientEmail);
  return !/^[\w.-]+@[\w.-]+\.\w{2,}$/.test(clientEmail);
}

function testIfItIsNotValidNrCRC(nrCRC) {
  const regex = /^\d{4}-\d{4}-\d{4}$/;
  return !regex.test(nrCRC);
}

function isNotValidLetters(str) {
  // remove os acentos
  const strNormalize = str.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  
  // RegEx Otimizada: Letras/espaços, opcionalmente seguidas por grupos que começam com um único delimitador e mais letras/espaços
  return !/^(?!\s*$)[a-zA-Zà-úÀ-Ú\s]+(?:[',.-][a-zA-Zà-úÀ-Ú\s]+)*$/.test(strNormalize);// NOSONAR - strNormalize controlados
}

const getters = {
  getForm: (state) => state.form,
  getActiveTab: (state) => state.activeTab,
  getActiveTabParticipantType: (state) => {
    let participantType = 1;
    const interventionTypeObj = {
      1: "Particular",
      2: "ENI",
      3: "Empresa",
      4: "Autónomo"
    };

    state.activeTab.form.map((fieldForms) => {
      fieldForms.fields.map((field) => {
        if (field.key.startsWith("idClienttype")) {
          participantType = field.value
        }
      }
      )
    });

    return interventionTypeObj[participantType];
  },
  getField: (state) => (key) => {
    let field;

    state.form.tabs.forEach((tab) => {
      tab.forms.forEach((form) => {
        const find = form.fields.find((f) => f.key === key);
        if (find) field = find;
      });
    });

    return field;
  },
  getMonthDropdown: () => {
    return monthsV2;
  },
  getYearDropdown: () => {
    return yearsV2;
  },
  getFormTabs: (state) => state.form.tabs,
  getDisabledActionButtons: (state) => state.disabledActionButtons,
  getLoading: (state) => state.loading,
  getActiveBankInfo: (state) => state.bankInfoType,
  getBankTypeOptions: (state) => state.bankTypeOptions,
  getParticipantOptions: (state) => state.participantOptions,
  getFormNationality: (state) => state.formNationality,
  getMaxTabsLength: (state) => state.form.tabs.length,
  getHasThreeNames: (state) => {
    Vue.$toast.clear();
    const tabFirstParticipant = state.form.tabs.find((tab) => tab.id === 4);
    const firstNameField = tabFirstParticipant.form[1].fields.find((field) => field.key === "firstName");
    //const firstSurnameField = tabFirstParticipant.form[1].fields.find((field) => field.key === "firstSurname");
    //const secondSurnameField = tabFirstParticipant.form[1].fields.find((field) => field.key === "secondSurname");
    //const isSpainForm = state.formNationality === "ES";

    firstNameField.hasError = !firstNameField?.value;
    //SIFA-2205 -firstSurname non required
    //firstSurnameField.hasError = !firstSurnameField?.value && isSpainForm;
    //secondSurnameField.hasError = !secondSurnameField?.value;

    const errors = [];

    if (!firstNameField?.value) {
      errors.push({
        key: "firstName",
        message: i18n.t("form.error.noFirstName"),
      });
    }
    //SIFA-2205 -firstSurname non required
    /*if (!firstSurnameField?.value && isSpainForm) {
      errors.push({
        key: "firstSurname",
        message: i18n.t("form.error.noFirstSurname"),
      });
    }
    //SIFA-2205 -secondSurname non required
    if (!secondSurnameField?.value) {
      errors.push({
        key: "secondSurname",
        message: i18n.t("form.error.noSecondSurname"),
      });
    }*/

    // Set error flag and display toasts for all missing fields
    errors.reverse().forEach(({ message }) => {
      Vue.$toast.open({
        message: message,
        dismissible: true,
        type: "error",
      });
    });

    //SIFA-2205 -firstSurname and secondSurname non required
    const hasError = !!(firstNameField?.value);
    //const hasError = !!(firstNameField?.value && secondSurnameField?.value);
    //isSpainForm ?!!(firstNameField?.value && firstSurnameField?.value && secondSurnameField?.value): !!(firstNameField?.value && secondSurnameField?.value);

    return hasError;
  },
  getIsCompanyAndHasParticipants: (state) => state.form.tabs.some((tab) => tab?.dynamicallyCreated),
  getCopyAddressObject: (state) => state.copyAddressObject
};

const mutations = {
  setForm(state, form) {
    state.form = form;
  },
  setActiveTab(state, tabId) {
    state.form.tabs = state.form.tabs.map((t) => {
      if (t.id == tabId) {
        t.active = true;
        // set active tab
        state.activeTab = t;
      } else {
        t.active = false;
      }
      return t;
    });
    // *Caso seja preciso scroll na mudança de tab
    // // scroll até o topo do elemento com ID "form-proposta"
    // const scrollableElement = document.getElementById('form-proposal');
    // if (scrollableElement) {
    //   scrollableElement.scrollTop = 0;
    // } else {
    //   // fallback para fazer scroll se o elemento não for encontrado
    //   window.scrollTo(0, 0);
    // }
  },
  setFieldValue(state, { key, value, options, onChangeActions }) {
    const proposalFinId = store.getters["ProposalsModule/getProposalFinId"];

    let forfeitProposalFlag = false;
    let historyBtnFlag = false;
    let logsBtnFlag = false;
    let updateCarDataFlag = false;
    let changeProposalStatusFlag = false;

    let icDocBtnFlag = false;

    state?.activeTab?.form?.forEach((form) => {
      //ensure that all fields has no error when some value changed
      form?.fields?.map((field) => { field.hasError = field?.required ? field?.hasError : false; return field })

      if (onChangeActions) {
        onChangeActions.map(async (changeAction) => {
          if (changeAction.type === "populateFields") {
            const selected = options.find((o) => o.selected);

            if (changeAction.fields.includes("ALL")) {
              //Percorre todas as keys de cada objeto de cada valor da dropdown - as keys têm que ser as mesmas dos inputs
              Object.keys(selected).forEach((key) => {
                // ignore utility keys
                if (!["selected", "text", "value", "disabled"].includes(key)) {
                  this.commit("FormModule/setFieldValue", {
                    key,
                    value: selected[key],
                  });
                }
              });
            } else {
              changeAction.fields.map((key) => {
                this.commit("FormModule/setFieldValue", {
                  key,
                  value: selected[key],
                });
              });
            }
          }

          // Alterar no componente BankInfo o tipo de banco
          if (changeAction.type === "updateBankInfo") {
            const { value } = options.find((o) => o.selected);
            this.commit("FormModule/setActiveBankInfo", { value: value, nationality: state.formNationality });
            const fieldIban = form.fields.find((f) => f.key === "NIB");
            const isNational = form.fields.find((f) => f.key === "isNational");
            fieldIban.value = value === 1 ? state.ibanNationalData?.value : state.ibanInternationalData?.value
            isNational.value = value === 1 ? "Nacional" : "Internacional";
          }

          // Dá update à entity na navbar e no form
          if (changeAction.type === "updateEntity") {
            const { action, getter, fieldKey, updateNavbarEntity = false } = changeAction;
            this.commit("FormModule/setFieldAttribute", { key: fieldKey, attribute: 'loading', value: true });
            const { canvasserIdEntity, canvasserEntityName } = options.find((o) => o.selected);

            if (updateNavbarEntity) {
              this.commit("AuthenticationModule/setUserEntityId", canvasserIdEntity);
            }

            this.commit("FormModule/setFieldAttribute", { key: "canvasserEntityName", attribute: 'value', value: canvasserEntityName });

            // Retorna field que queremos atualizar
            let field;
            state.form.tabs.forEach((tab) => {
              tab.form.forEach((form) => {
                const find = form.fields.find((f) => f.key === fieldKey);
                if (find) field = find;
              });
            });

            // update do options do field consoante a entity escolhida
            await this.dispatch(action);
            let bmDropdownValues = store.getters[getter];

            // selecionar a opção default
            bmDropdownValues = bmDropdownValues.map((value) => {
              value.selected = value[fieldKey] === field.value;
              return value;
            });

            // caso não exista valor default nas novas options, selecionar value -1
            if (!bmDropdownValues.find((value) => value.selected)) {
              bmDropdownValues[0].selected = true;
              field.value = bmDropdownValues[0].value;
            }

            this.commit("FormModule/setFieldAttribute", { key: fieldKey, attribute: 'options', value: bmDropdownValues });
            this.commit("FormModule/setFieldValue", field);
            this.commit("FormModule/setIncrementField", { key: fieldKey });
            this.commit("FormModule/setFieldAttribute", { key: fieldKey, attribute: 'loading', value: false });
          }

          // Toggle de visibilidade da área dos dados da morada fiscal
          if (changeAction.type === "enableTaxAddressInfo") {
            const field = form.fields.find((f) => f.key === key);
            field.value = value;
            form.fields = form.fields.map((field) => {
              if (field.type !== "neo-toggle") {
                field.editable = value !== 1;
                field.required = value !== 1;
                field.value = "";
                if (field.type === "dropdown") field.value = -1;
              }
              // ! Temporário - remover if (field.key.startsWith("address2_country") && address1SameAddress2.value !== 1) 
              if (field.key.startsWith("address2_country") && value !== 1) {
                field.value = "Portugal";
                field.keyReRender++;
              }
              return field;
            });
          }

          // Visibilidade dos campos de identificação consoante a nacionalidade
          if (changeAction.type === "enableDisableDni") {
            switch (state.formNationality) {
              case "ES":
                //SIFA-687
                handleESProposalIdFields(options, state, form, key, value);
                break;
              case "PT":
                //SIFA-1064
                handlePTProposalIdFields(options, state, form, key, value);
                break;
            }
          }

          if (changeAction.type === "enableDisableIdentificationType") {
            const { neiField, dtExpireResidenceAuthorizationText, dniField, dtExpireCCText, emissionEntityCC, numberPassport, dtExpirePassport } = getPTIDFields(state);
            const { dtEmissionCCText, dtEmissionResidenceAuthorizationText } = getESIDFields(state);
            const nationality = state.activeTab.form.find((form) => form?.key?.startsWith("personalData"))?.fields.find((field) => field?.key?.startsWith("nationality"));
            
            const isPortugal = nationality?.value?.toLowerCase() === "portugal";
            if(state.formNationality === 'PT') {
              options = options?.map(item => ({
                ...item,
                disabled: (!isPortugal && item.value === 0) || item.value === -1
              }));
            }

            const isSpain = nationality?.value?.toLowerCase() === "españa";
            if(state.formNationality === 'ES') {
              options = options?.map(item => ({
                ...item,
                disabled: (!isSpain && item.value === 0) || item.value === -1
              }));
            }

            const field = form.fields.find((f) => f?.key === key);
            if(field) {
              if (field?.type === "dropdown") {
                field.value = value?.text;
              }

              this.commit("FormModule/setFieldAttribute", { key: key, attribute: 'options', value: options });
              this.commit("FormModule/setFieldValue", field.value);
              this.commit("FormModule/setIncrementField", { key: key });

              switch (field.value) {
                case "BI/Cartão de Cidadão":
                case "Cartão de Cidadão":
                case "B.I. Civil":
                  this.commit("FormModule/setFieldAttribute", { key: nationality.key, attribute: 'value', value: "Portugal" });
                  this.commit("FormModule/setIncrementField", { key: nationality.key });
                  field.editable = false;
                  // BI/CC
                  setFieldsVisibilityAndRequirements({ dniField, dtExpireCCText, emissionEntityCC }, true);
                  // Titulo de residência
                  setFieldsVisibilityAndRequirements({ neiField, dtExpireResidenceAuthorizationText }, false);
                  // Passaporte
                  setFieldsVisibilityAndRequirements({ numberPassport, dtExpirePassport }, false);
                  break;
                case "Passaporte":
                  // Passaporte
                  setFieldsVisibilityAndRequirements({ numberPassport, dtExpirePassport }, true);
                  // Titulo de residência
                  setFieldsVisibilityAndRequirements({ neiField, dtExpireResidenceAuthorizationText }, false);
                  // BI/CC
                  setFieldsVisibilityAndRequirements({ dniField, dtExpireCCText, emissionEntityCC }, false);
                  break;
                case "Autorização de residência":
                case "Titulo de residência":
                case "Autorização de residência CPLP":
                case "Autorização de Residência CPLP":
                case "Titulo/Cartão Residência Temporário":
                case "Cartão Cidadão Tratado Porto Seguro":
                case "Certificado de Registo EU":
                case "Titulo/Cartão Residência Permanente":
                case "Autorizacao de Residencia Perm.":
                case "Autorizacao de Residencia Temp.":
                case "Cartão do Cidadão Porto Seguro":
                case "Guia de Residência - Provisório":
                  // Titulo de residência
                  setFieldsVisibilityAndRequirements({ neiField, dtExpireResidenceAuthorizationText }, true);
                  // BI/CC
                  setFieldsVisibilityAndRequirements({ dniField, dtExpireCCText, emissionEntityCC }, false);
                  // Passaporte
                  setFieldsVisibilityAndRequirements({ numberPassport, dtExpirePassport }, false);
                  break;
                case "DNI":
                  setFieldsVisibilityAndRequirements({ dniField, dtExpireCCText, dtEmissionCCText }, true);
                  setFieldsVisibilityAndRequirements({ neiField, dtExpireResidenceAuthorizationText, dtEmissionResidenceAuthorizationText }, false);
                  break;
                case "NIE":
                  setFieldsVisibilityAndRequirements({ neiField, dtExpireResidenceAuthorizationText, dtEmissionResidenceAuthorizationText }, true);
                  setFieldsVisibilityAndRequirements({ dniField, dtEmissionCCText, dtExpireCCText }, false);
                  break;
              }
            }
          }

          if (changeAction.type === "onChangeCarDtPlate") {
            const field = form.fields.find((f) => f.key === key);
            field.value = value;

            if (field.value) {
              const dateParsed = new Date(field.value);
              const carMonth = form.fields.find((f) => f.key === "carMonth");
              const carYear = form.fields.find((f) => f.key === "carYear");
              carMonth.value = String(dateParsed.getMonth() + 1);
              carYear.value = String(dateParsed.getFullYear());
            }
          }

          if (changeAction.type === "onChangeRelationToProponent") {
            // https://dtogsc.atlassian.net/browse/SIFA-1647
            // Se o titular for Empresa & for adicionado um avalista ou titular com relação com o primeiro titular: sócio - então retirar dados de secção

            const field = form.fields.find((f) => f?.key === key);
            if(field) {
              field.value = value?.text;
            }
            

            const isACompanyFirstParticipant = store.getters["ProposalsModule/getIsCompanyClientType"];
            const firstParticipantType = store.getters["ProposalsModule/getProposalClientType"];
            const isAENIFirstParticipant = firstParticipantType?.clientTypeId === 2;
            if (state.formNationality == "PT") {
              // // Dados Pessoais Detalhados
              // https://dtogsc.atlassian.net/browse/SIFA-1679
              // Repor o campo: Estado Cívil para o sócio
              // const personalInfoSection = state.activeTab.form.find((form) => form?.key?.startsWith("personalInfo"));
              // // Habitação
              // const residenceDataSection = state.activeTab.form.find((form) => form?.key?.startsWith("residenceData"));
              // Dados de rendimento
              const profitDataSection = state.activeTab.form.find((form) => form?.key?.startsWith("profit"));
              const quota = form.fields.find((f) => f?.key?.startsWith("quota"));
              //Quando o primeiro titular é empresa e os restantes titulares são do tipo "Sócio"
              const preConditionToSectionShow = (isACompanyFirstParticipant || isAENIFirstParticipant) && (field?.value === "Sócio" || field?.value === "Beneficiário" || field?.value === "Representante");
             
              const updateField = (field) => {
                field.fieldShow = !preConditionToSectionShow && field?.proposalType?.includes("PT");
               
                if(field.key?.startsWith("isSickLeave")) {
                  field.value = 2;
                  this.commit("FormModule/setIncrementField", { key: field?.key });
                }

                if(field.key?.startsWith("hasFullProofIncome")) {
                  field.value = 1;
                  this.commit("FormModule/setIncrementField", { key: field?.key });
                }

                if (!field.fieldShow && field?.type !== 'neo-toggle') {
                  field.value = "";
                }
                return field;
              };

              // personalInfoSection?.fields?.map(updateField);
              // residenceDataSection?.fields?.map(updateField);
              profitDataSection?.fields?.map(updateField);
              if (quota) {
                quota.required = preConditionToSectionShow;
                quota.editable = preConditionToSectionShow;
                quota.value = preConditionToSectionShow ? quota.value : "";
              }
            }
          }

          // Profesión - estudante, o tipo de contrato não é obrigatório 
          if (changeAction.type === "enableDisableContractType") {
            switch (proposalFinId) {
              //Sofinco
              case FINANCIAL_IDS.SOFINCO:
                sofincoProfessionalCategoryValidation(form, key, state, value);
                break;
              //BBVA ES
              case FINANCIAL_IDS.BBVA_ESPANHA:
                bbvaEsProfessionalCategoryValidation(form, key, state, value);
                break;
              //Caixabank
              case FINANCIAL_IDS.CAIXABANK:
                caixabankProfessionalCategoryValidation(form, key, state, value);
                break;
              //Cetelem espanha
              case FINANCIAL_IDS.CETELEM_ES:
                cetelemSpainProfessionalCategoryValidation(form, key, state, value);
                break;
              case FINANCIAL_IDS.SABADELL:
                sabadellProfessionalCategoryValidation(form, key, state, value);
                break;
              case FINANCIAL_IDS.SANTANDER_CONSUMER_ES:
                santanderEsProfessionalCategoryValidation(form, key, state, value);
                break;
              //PT cases
              case FINANCIAL_IDS.BBVA:
                bbvaPtProfessionalCategoryValidation(form, key, state, value);
                break;
              case FINANCIAL_IDS.COFIDIS_PT:
                cofidisPtProfessionalCategoryValidation(form, key, state, value);
                 break;
              case 4:
              case 6:
              case 7:
              case 8:
              case 9:
                ptProfessionalCategoryValidation(form, key, state, value, proposalFinId);
                break;
            }
          }

          // Triggers do tipo de contrato SIFA-1256
          if (changeAction.type === "enableDisableEndDateActivity") {
            //Quando simplesmente atribui o valor ao campo, sem validações
            const fieldValueAttribution = () => {
              const field = form.fields.find((f) => f?.key === key);
              if(field) {
                field.value = value?.text;
              }
              
            }
            switch (proposalFinId) {
              case 12:
              case 13:
              case 15:
              case 16:
              case 17:
              case 18:
                fieldValueAttribution();
                if(proposalFinId == 16) {
                  esContractTypeValidation(form, key, state, value);
                }
                break;
              case FINANCIAL_IDS.BBVA:
              case FINANCIAL_IDS.SANTANDER_CONSUMER:
              case FINANCIAL_IDS.BPI:
              case FINANCIAL_IDS.CGD:
              case FINANCIAL_IDS.CETELEM:
              case FINANCIAL_IDS.CREDIBOM:
              case FINANCIAL_IDS.COFIDIS_PT:
                ptContractTypeValidation(form, key, state, value);
                break;
            }
          }

          // Triggers do regime de bens SIFA-1256
          if (changeAction.type === "enableDisableMarriageType") {
            //Quando simplesmente atribui o valor ao campo, sem validações
            const fieldValueAttribution = () => {
              const field = form.fields.find((f) => f?.key === key);
              if(field) {
                field.value = value?.text;
              }
            }
            switch (proposalFinId) {
              case 12:
              case 13:
              case 15:
              case 16:
              case 17:
              case 18:
                fieldValueAttribution();
                break;
              //PT cases
              case FINANCIAL_IDS.BBVA:
              case FINANCIAL_IDS.SANTANDER_CONSUMER:
              case FINANCIAL_IDS.BPI:
              case FINANCIAL_IDS.CGD:
              case FINANCIAL_IDS.CETELEM:
              case FINANCIAL_IDS.CREDIBOM:
              case FINANCIAL_IDS.COFIDIS_PT:
                ptMaritalStateValidation(form, key, state, value);
                break;
            }
          }

          if (changeAction.type === "enableDisableCompanySector"){
            if (proposalFinId === FINANCIAL_IDS.SABADELL){
              sabadellCompanySectorValidation(form, key, state, value);
            } else if (proposalFinId === FINANCIAL_IDS.COFIDIS_PT){

              const field = form.fields.find((f) => f?.key === key);
              if(field) {
                field.value = value?.text;
                cofidisJobValidation(form, key, state, value);
              }
            } else {
              const field = form.fields.find((f) => f.key === key);
              if (field)
                field.value = value?.text;
            }
          }

          if (changeAction.type === "sendDropdownValue") {
            const field = form.fields.find((f) => f?.key === key);
            if(field) {
              field.value = value?.value;
            }
          }

          // Handler de Código postais - Empresa 
          if (changeAction.type === "fetchCompanyPostalCode") {
            const city = form.fields.find((f) => f?.key?.startsWith("city"));
            const postalCode1 = form.fields.find((f) => f?.key?.startsWith("postalCode1") && f?.proposalType?.includes(state.formNationality));
            
            const addressDistrict = form.fields.find((f) => f?.key?.startsWith("addressMunicipality"));
            const addressMunicipality = form.fields.find((f) => f?.key?.startsWith("addressDistrict"));
            
            if(postalCode1) {
              if (changeAction.typeOfPostalCode === "PT") {
                //! PT código postal - é enviado no formulário o postalCode1 e postalCode2
                const postalCode2 = form.fields.find((f) => f?.key?.startsWith("postalCode2") && f?.proposalType?.includes(state.formNationality));
                if (key.startsWith("postalCode1")) {
                  postalCode1.value = value;
                } else if (key.startsWith("postalCode2")) {
                  postalCode2.value = value;
                }
                // Verifica se foi atingido o limite dos caracteres de ambos os campos para fazer a pesquisa do código postal
                if (postalCode1?.maxLength === postalCode1?.value?.length && postalCode2?.maxLength === postalCode2?.value?.length) {
                  const postalCodeValue = postalCode1?.value + postalCode2?.value;
                  const { districtValue, municipalityValue } = await store.dispatch("ProposalsModule/getSearchByPostalCode", { formNationality: state.formNationality, localityFieldKey: city.key, postalCode: postalCodeValue });
                  //Cidade
                  city.loading = false;

                  //Distrito
                  addressDistrict.value = districtValue;
                  addressDistrict.editable = !districtValue;
                  addressDistrict.hasError = false;

                  //Municipio
                  addressMunicipality.value = municipalityValue;
                  addressMunicipality.editable = !municipalityValue;
                  addressMunicipality.hasError = false;
  
                  const cityValues = store.getters["ProposalsModule/getSearchByPostalCode"](city.key);
                  if (cityValues?.length > 1) {
                    if (cityValues?.length === 2) {
                      cityValues[1].selected = true;
  
                      // Create a promise to handle the asynchronous update
                      const updateCityValue = new Promise((resolve) => {
                        resolve(cityValues[1]?.text);
                      });
  
                      // Update city.value when the promise is resolved
                      updateCityValue.then((updatedValue) => {
                        city.value = updatedValue;
                      });
                    }
                    this.commit("FormModule/setFieldAttribute", { key: city?.key, attribute: 'options', value: cityValues });
                    this.commit("FormModule/setFieldValue", city);
                    this.commit("FormModule/setIncrementField", { key: city?.key });
                  } else {
                    city.type = "text";
                  }
                } else {
                  //Distrito
                  addressDistrict.value = "";
                  addressDistrict.editable = true;
                  //Municipio
                  addressMunicipality.value = "";
                  addressMunicipality.editable = true;

                  //Cidade
                  city.value = "";
                  this.commit("FormModule/setFieldAttribute", { key: city?.key, attribute: 'options', value: [] });
                  city.loading = true;
                  this.commit("FormModule/setIncrementField", { key: city?.key });
                }
              }
            }
          }

          // Handler de Código postais - Empresa (Dados de correspondência)
          if (changeAction.type === "fetchCompanyMailPostalCode") {
            const mailCity = form.fields.find((f) => f?.key?.startsWith("mailCity"));
            const mailPostalCode1 = form.fields.find((f) => f?.key?.startsWith("mailPostalCode1") && f?.proposalType?.includes(state.formNationality));
            if (changeAction.typeOfPostalCode === "PT") {
              //! PT código postal - é enviado no formulário o mailPostalCode1 e postalCode2
              const mailPostalCode2 = form.fields.find((f) => f?.key?.startsWith("mailPostalCode2") && f?.proposalType?.includes(state.formNationality));
              if (key.startsWith("mailPostalCode1")) {
                mailPostalCode1.value = value;
              } else if (key.startsWith("mailPostalCode2")) {
                mailPostalCode2.value = value;
              }
              // Verifica se foi atingido o limite dos caracteres de ambos os campos para fazer a pesquisa do código postal
              if (mailPostalCode1?.maxLength === mailPostalCode1?.value?.length && mailPostalCode2?.maxLength === mailPostalCode2?.value?.length) {
                const postalCodeValue = mailPostalCode1?.value + mailPostalCode2?.value;
                await store.dispatch("ProposalsModule/getSearchByPostalCode", { formNationality: state.formNationality, localityFieldKey: mailCity.key, postalCode: postalCodeValue });
                //Cidade
                mailCity.loading = false;

                const mailCityValues = store.getters["ProposalsModule/getSearchByPostalCode"](mailCity.key);
                if (mailCityValues.length === 2) {
                  mailCityValues[1].selected = true;

                  // Create a promise to handle the asynchronous update
                  const updateAddress1CityValue = new Promise((resolve) => {
                    resolve(mailCityValues[1]?.text);
                  });

                  // Update mailCity.value when the promise is resolved
                  updateAddress1CityValue.then((updatedValue) => {
                    mailCity.value = updatedValue;
                    console.log(mailCityValues[1]?.text, mailCity.value);
                  });
                }
                this.commit("FormModule/setFieldAttribute", { key: mailCity?.key, attribute: 'options', value: mailCityValues });
                this.commit("FormModule/setFieldValue", mailCity);
                this.commit("FormModule/setIncrementField", { key: mailCity?.key });
              } else {
                //Cidade
                mailCity.value = "";
                this.commit("FormModule/setFieldAttribute", { key: mailCity?.key, attribute: 'options', value: [] });
                mailCity.loading = true;
                this.commit("FormModule/setIncrementField", { key: mailCity?.key });
              }
            }
          }

          if (changeAction.type === "fetchPostalCodeDataAddress1") {
            const address1City = form.fields.find((f) => f?.key?.startsWith("address1_city"));
            const address1District = form.fields.find((f) => f?.key?.startsWith("address1_district"));
            const address1PostalCode1 = form.fields.find((f) => f?.key?.startsWith("address1_postalCode1") && f?.proposalType?.includes(state.formNationality));
            const address1Municipality = form.fields.find((f) => f?.key?.startsWith("address1_municipality"));

            if(address1PostalCode1) {
              if (changeAction.typeOfPostalCode === "PT") {
                //! PT código postal - é enviado no formulário o address1PostalCode1 e address1PostalCode2
                const address1PostalCode2 = form.fields.find((f) => f?.key?.startsWith("address1_postalCode2") && f?.proposalType?.includes(state.formNationality));
                if (key.startsWith("address1_postalCode1")) {
                  address1PostalCode1.value = value;
                } else if (key.startsWith("address1_postalCode2")) {
                  address1PostalCode2.value = value;
                }
                // Verifica se foi atingido o limite dos caracteres de ambos os campos para fazer a pesquisa do código postal
                if (address1PostalCode1?.maxLength === address1PostalCode1?.value?.length && address1PostalCode2?.maxLength === address1PostalCode2?.value?.length) {
                  const postalCodeValue = address1PostalCode1?.value + address1PostalCode2?.value;
                  const { districtValue, municipalityValue } = await store.dispatch("ProposalsModule/getSearchByPostalCode", { formNationality: state.formNationality, localityFieldKey: address1City.key, postalCode: postalCodeValue });
  
                  // Colocar este campo com o tipo de dropdown
                  this.commit("FormModule/setFieldAttribute", { key: address1City?.key, attribute: 'type', value: "dropdown" });
  
                  //Distrito
                  address1District.value = districtValue;
                  address1District.editable = !districtValue;
                  address1District.hasError = false;
                  //Cidade
                  address1City.loading = false;
                  address1PostalCode1.hasError = false;
                  //Municipio
                  address1Municipality.value = municipalityValue;
                  address1Municipality.editable = !municipalityValue;
                  address1Municipality.hasError = false;
  
                  const cityValues = store.getters["ProposalsModule/getSearchByPostalCode"](address1City.key);
                  if (cityValues?.length > 1) {
                    if (cityValues?.length === 2) {
                      cityValues[1].selected = true;
  
                      // Create a promise to handle the asynchronous update
                      const updateAddress1CityValue = new Promise((resolve) => {
                        resolve(cityValues[1]?.text);
                      });
  
                      // Update address1City.value when the promise is resolved
                      updateAddress1CityValue.then((updatedValue) => {
                        address1City.value = updatedValue;
                        console.log(cityValues[1]?.text, address1City.value);
                      });
                    }
                    this.commit("FormModule/setFieldAttribute", { key: address1City?.key, attribute: 'options', value: cityValues });
                    this.commit("FormModule/setFieldValue", address1City);
                    this.commit("FormModule/setIncrementField", { key: address1City?.key });
                  } else {
                    address1City.type = "text";
                  }
                } else {
                  //Distrito
                  address1District.value = "";
                  address1District.editable = true;
                  //Municipio
                  address1Municipality.value = "";
                  address1Municipality.editable = true;
                  //Cidade
                  address1City.value = "";
                  this.commit("FormModule/setFieldAttribute", { key: address1City?.key, attribute: 'options', value: [] });
                  // Colocar este campo com o tipo de text
                  this.commit("FormModule/setFieldAttribute", { key: address1City?.key, attribute: 'type', value: "text" });
                  address1City.loading = true;
                  this.commit("FormModule/setIncrementField", { key: address1City?.key });
                }
              } else {
                //! ES código postal - apenas é enviado no formulário o address1PostalCode1
                if (address1PostalCode1?.maxLength === value?.length) {
                  const { districtValue } = await store.dispatch("ProposalsModule/getSearchByPostalCode", { formNationality: state.formNationality, localityFieldKey: address1City.key, postalCode: value });
  
                  // Colocar este campo com o tipo de dropdown
                  this.commit("FormModule/setFieldAttribute", { key: address1City?.key, attribute: 'type', value: "dropdown" });
                  //Distrito
                  address1District.value = districtValue;
                  address1District.editable = !districtValue;
                  address1District.hasError = false;
                  //Cidade
                  address1City.loading = false;
                  address1PostalCode1.hasError = false;
  
                  const cityValues = store.getters["ProposalsModule/getSearchByPostalCode"](address1City.key);
  
                  if (cityValues?.length > 1) {
                    if (cityValues.length === 2) {
                      cityValues[1].selected = true;
  
                      // Create a promise to handle the asynchronous update
                      const updateAddress1CityValue = new Promise((resolve) => {
                        resolve(cityValues[1]?.text);
                      });
  
                      // Update address1City.value when the promise is resolved
                      updateAddress1CityValue.then((updatedValue) => {
                        address1City.value = updatedValue;
                        console.log(cityValues[1]?.text, address1City.value);
                      });
                    }
                    this.commit("FormModule/setFieldAttribute", { key: address1City?.key, attribute: 'options', value: cityValues });
                    this.commit("FormModule/setFieldValue", address1City);
                    this.commit("FormModule/setIncrementField", { key: address1City?.key });
                  } else {
                    address1City.type = "text";
                  }
                } else {
                  //Distrito
                  address1District.value = "";
                  address1District.editable = true;
                  //Cidade
                  address1City.value = "";
                  this.commit("FormModule/setFieldAttribute", { key: address1City?.key, attribute: 'options', value: [] });
                  // Colocar este campo com o tipo de text
                  this.commit("FormModule/setFieldAttribute", { key: address1City?.key, attribute: 'type', value: "text" });
                  address1City.loading = true;
                  this.commit("FormModule/setIncrementField", { key: address1City?.key });
                }
                address1PostalCode1.value = value;
              }
            }
          }

          if (changeAction.type === "fetchPostalCodeDataAddress2") {
            const address2City = form.fields.find((f) => f?.key?.startsWith("address2_city"));
            const address2District = form.fields.find((f) => f?.key?.startsWith("address2_district"));
            const address2PostalCode1 = form.fields.find((f) => f?.key?.startsWith("address2_postalCode1") && f?.proposalType?.includes(state.formNationality));
            const address2Municipality = form.fields.find((f) => f?.key?.startsWith("address2_municipality"));

            if (changeAction.typeOfPostalCode === "PT") {
              //! PT código postal - é enviado no formulário o address2PostalCode1 e address2PostalCode2
              const address2PostalCode2 = form.fields.find((f) => f?.key?.startsWith("address2_postalCode2") && f?.proposalType?.includes(state.formNationality));
              if (key.startsWith("address2_postalCode1")) {
                address2PostalCode1.value = value;
              } else if (key.startsWith("address2_postalCode2")) {
                address2PostalCode2.value = value;
              }
              // Verifica se foi atingido o limite dos caracteres de ambos os campos para fazer a pesquisa do código postal
              if (address2PostalCode1?.maxLength === address2PostalCode1?.value?.length && address2PostalCode2?.maxLength === address2PostalCode2?.value?.length) {
                const postalCodeValue = address2PostalCode1?.value + address2PostalCode2?.value;
                const { districtValue, municipalityValue } = await store.dispatch("ProposalsModule/getSearchByPostalCode", { formNationality: state.formNationality, localityFieldKey: address2City.key, postalCode: postalCodeValue });
                //Distrito
                address2District.value = districtValue;
                address2District.editable = !districtValue;
                address2District.hasError = false;
                //Cidade
                address2City.loading = false;
                address2PostalCode1.hasError = false;
                //Municipio
                address2Municipality.value = municipalityValue;
                address2Municipality.editable = !municipalityValue;
                address2Municipality.hasError = false;

                const cityValues = store.getters["ProposalsModule/getSearchByPostalCode"](address2City.key);
                console.log(cityValues);
                if (cityValues?.length > 1) {
                  if (cityValues?.length === 2) {
                    cityValues[1].selected = true;

                    // Create a promise to handle the asynchronous update
                    const updateAddress1CityValue = new Promise((resolve) => {
                      resolve(cityValues[1]?.text);
                    });

                    // Update address1City.value when the promise is resolved
                    updateAddress1CityValue.then((updatedValue) => {
                      address2City.value = updatedValue;
                    });
                  }
                  address2City.type = "dropdown";

                  this.commit("FormModule/setFieldAttribute", { key: address2City?.key, attribute: 'options', value: cityValues });
                  this.commit("FormModule/setFieldValue", address2City);
                  this.commit("FormModule/setIncrementField", { key: address2City?.key });
                } else {
                  address2City.type = "text";
                }
                console.log(address2City.type);
                console.log(address2City);
              } else {
                //Distrito
                address2District.value = "";
                address2District.editable = true;
                //Municipio
                address2Municipality.value = "";
                address2Municipality.editable = true;
                //Cidade
                address2City.value = "";
                this.commit("FormModule/setFieldAttribute", { key: address2City?.key, attribute: 'options', value: [] });
                address2City.loading = true;
                this.commit("FormModule/setIncrementField", { key: address2City?.key });
              }
            } else {
              //! ES código postal - apenas é enviado no formulário o address2PostalCode1
              if (address2PostalCode1?.maxLength === value?.length) {
                const { districtValue } = await store.dispatch("ProposalsModule/getSearchByPostalCode", { formNationality: state.formNationality, localityFieldKey: address2City.key, postalCode: value });
                //Distrito
                address2District.value = districtValue;
                address2District.editable = !districtValue;
                address2District.hasError = false;
                //Cidade
                address2City.loading = false;
                address2PostalCode1.hasError = false;

                const cityValues = store.getters["ProposalsModule/getSearchByPostalCode"](address2City.key);
                if (cityValues?.length > 1) {
                  if (cityValues?.length === 2) {
                    cityValues[1].selected = true;

                    // Create a promise to handle the asynchronous update
                    const updateAddress1CityValue = new Promise((resolve) => {
                      resolve(cityValues[1]?.text);
                    });

                    // Update address1City.value when the promise is resolved
                    updateAddress1CityValue.then((updatedValue) => {
                      address2City.value = updatedValue;
                      console.log(cityValues[1]?.text, address2City.value);
                    });
                  }
                  address2City.type = "dropdown";
                  this.commit("FormModule/setFieldAttribute", { key: address2City?.key, attribute: 'options', value: cityValues });
                  this.commit("FormModule/setFieldValue", address2City);
                  this.commit("FormModule/setIncrementField", { key: address2City?.key });
                } else {
                  address2City.type = "text";
                }
              } else {
                //Distrito
                address2District.value = "";
                address2District.editable = true;
                //Cidade
                address2City.value = "";
                this.commit("FormModule/setFieldAttribute", { key: address2City?.key, attribute: 'options', value: [] });
                address2City.loading = true;
                this.commit("FormModule/setIncrementField", { key: address2City?.key });
              }
              address2PostalCode1.value = value;
            }
          }

          if (changeAction.type === "fetchPostalCodeCompanyData") {
            const companyCity = form.fields.find((f) => f?.key?.startsWith("companyCity"));
            const companyPostalCode1 = form.fields.find((f) => f?.key?.startsWith("companyPostalCode1") && f?.proposalType?.includes(state.formNationality));

            if (companyPostalCode1?.maxLength === value?.length) {
              await store.dispatch("ProposalsModule/getSearchByPostalCode", { formNationality: state.formNationality, localityFieldKey: companyCity.key, postalCode: value });

              // Colocar este campo com o tipo de dropdown
              this.commit("FormModule/setFieldAttribute", { key: companyCity?.key, attribute: 'type', value: "dropdown" });
              companyCity.loading = false;

              const cityValues = store.getters["ProposalsModule/getSearchByPostalCode"](companyCity.key);

              if (cityValues?.length > 1) {
                if (cityValues?.length === 2) {
                  cityValues[1].selected = true;

                  // Create a promise to handle the asynchronous update
                  const updateAddress1CityValue = new Promise((resolve) => {
                    resolve(cityValues[1]?.text);
                  });

                  // Update address1City.value when the promise is resolved
                  updateAddress1CityValue.then((updatedValue) => {
                    companyCity.value = updatedValue;
                    console.log(cityValues[1]?.text, companyCity.value);
                  });
                }
                this.commit("FormModule/setFieldAttribute", { key: companyCity?.key, attribute: 'options', value: cityValues });
                this.commit("FormModule/setFieldValue", companyCity);
                this.commit("FormModule/setIncrementField", { key: companyCity?.key });
              } else {
                companyCity.type = "text";
              }
            }
            if(companyPostalCode1) {
              companyPostalCode1.value = value;
            }
          }

          if (changeAction.type === "fetchPostalCompanyCodeData") {
            const companyCity = form.fields.find((f) => f?.key?.startsWith("companyCity"));
            const companyPostalCode1 = form.fields.find((f) => f?.key?.startsWith("companyPostalCode1") && f?.proposalType?.includes(state.formNationality));

            if (changeAction.typeOfPostalCode === "PT") {
              //! PT código postal - é enviado no formulário o companyPostalCode1 e companyPostalCode2
              const companyPostalCode2 = form.fields.find((f) => f?.key?.startsWith("companyPostalCode2") && f?.proposalType?.includes(state.formNationality));
              if (key.startsWith("companyPostalCode1")) {
                companyPostalCode1.value = value;
              } else if (key.startsWith("companyPostalCode2")) {
                companyPostalCode2.value = value;
              }
              // Verifica se foi atingido o limite dos caracteres de ambos os campos para fazer a pesquisa do código postal
              if (companyPostalCode1?.maxLength === companyPostalCode1?.value?.length && companyPostalCode2?.maxLength === companyPostalCode2?.value?.length) {
                const postalCodeValue = companyPostalCode1?.value + companyPostalCode2?.value;
                await store.dispatch("ProposalsModule/getSearchByPostalCode", { formNationality: state.formNationality, localityFieldKey: companyCity.key, postalCode: postalCodeValue });
                //Cidade
                companyCity.loading = false;
                companyPostalCode1.hasError = false;
                const cityValues = store.getters["ProposalsModule/getSearchByPostalCode"](companyCity.key);
                if (cityValues?.length > 1) {
                  if (cityValues?.length === 2) {
                    cityValues[1].selected = true;

                    // Create a promise to handle the asynchronous update
                    const updateAddress1CityValue = new Promise((resolve) => {
                      resolve(cityValues[1]?.text);
                    });

                    // Update address1City.value when the promise is resolved
                    updateAddress1CityValue.then((updatedValue) => {
                      companyCity.value = updatedValue;
                      console.log(cityValues[1]?.text, companyCity.value);
                    });
                  }
                  this.commit("FormModule/setFieldAttribute", { key: companyCity?.key, attribute: 'options', value: cityValues });
                  this.commit("FormModule/setFieldValue", companyCity);
                  this.commit("FormModule/setIncrementField", { key: companyCity?.key });
                } else {
                  companyCity.type = "text";
                }
              } else {
                //Cidade
                companyCity.value = "";
                this.commit("FormModule/setFieldAttribute", { key: companyCity?.key, attribute: 'options', value: [] });
                companyCity.loading = true;
                this.commit("FormModule/setIncrementField", { key: companyCity?.key });
              }
            }
          }

          if (changeAction.type === "fetchJobs") {
            const professionalCategory = form.fields.find((f) => f.key === key);
            const professionalJobs = form.fields.find((f) => f?.key?.startsWith("job"));

            if (professionalCategory) {
              if (value?.value !== -1) {
                const idClienttype = state.activeTab?.form?.find((form) => form?.key?.startsWith('generalData'))?.fields?.find((field) => field?.key?.startsWith('idClienttype'));
                const participantType = idClienttype.value; 

                professionalCategory.value = value?.text;
                let profCategoryValue = professionalCategory.value;
                if(proposalFinId == 16 && participantType == 4 && profCategoryValue == 'Rentista') {
                  profCategoryValue = profCategoryValue + "-" + participantType;
                }
                
                const optionsProfessionalJobs = await store.dispatch("ProposalsModule/getJobsWithCategory", { finId: proposalFinId, jobCategory: profCategoryValue, fieldKey: professionalCategory.key });

                professionalJobs.value = "";
                professionalJobs.required = optionsProfessionalJobs.length !== 1;
                professionalJobs.editable = optionsProfessionalJobs.length !== 1;
                if (optionsProfessionalJobs.length === 1) professionalJobs.hasLoading = false;

                this.commit("FormModule/setFieldAttribute", { key: professionalJobs?.key, attribute: 'options', value: optionsProfessionalJobs });
                this.commit("FormModule/setFieldValue", professionalJobs);
                this.commit("FormModule/setIncrementField", { key: professionalJobs?.key });
              } else {
                professionalCategory.value = "";
              }
            }

          }

          // Empresa cotada em bolsa - sim ou não. 
          // Abre um campo - qual é o nome da bolsa. 
          // para a Cetelem enviar no serviço: listedCompanyMarketNameCode - se for sim - o campo - Empresa cotada em bolsa
          // https://dtogsc.atlassian.net/browse/SIFA-1116
          if (changeAction.type === "isListedCompanyAction") {
            const isListedCompany = form.fields.find((f) => f.key === key);
            const sickLeaveReason = form.fields.find((f) => f?.key?.startsWith("sickLeaveReason"));

            if(isListedCompany){
              isListedCompany.value = value;
              sickLeaveReason.fieldShow = value === 1;
              sickLeaveReason.required = value === 1;
  
              sickLeaveReason.value = "";
              sickLeaveReason?.options.map((option) => option.selected = false);
              sickLeaveReason.options[0].selected = true;
  
              sickLeaveReason.keyReRender++;
            }
          }

          if (changeAction.type === "namesRequirementsSetting") {
            const generalDataSection = state?.activeTab?.form?.find((formFound) => formFound?.key?.startsWith('generalData'));
            const nationality = form.fields.find((f) => f.key === key);
            const firstName = generalDataSection?.fields?.find((f) => f?.key?.startsWith("firstName"));
            const firstSurname = generalDataSection?.fields?.find((f) => f?.key?.startsWith("firstSurname"));
            const secondSurname = generalDataSection?.fields?.find((f) => f?.key?.startsWith("secondSurname"));

            switch (state.formNationality) {
              case "ES":
                if (nationality?.value === "Espanha" || nationality?.value === "ESPANHA" || nationality?.value === "España" || nationality?.value === "ESPAÑA") {
                  firstName.required = true;
                  firstSurname.required = true;
                  secondSurname.required = true;
                } else {
                  //Reset
                  firstName.required = true;
                  firstSurname.required = true;
                  secondSurname.required = false;
                }
                break;
              case "PT":
                if (nationality?.value === "Espanha" || nationality?.value === "ESPANHA" || nationality?.value === "España" || nationality?.value === "ESPAÑA") {
                  firstName.required = true;
                  firstSurname.required = true;
                  secondSurname.required = true;
                } else {
                  //Reset
                  firstName.required = true;
                  firstSurname.required = false;
                  secondSurname.required = true;
                }
                break;
            }
          }

          if (form.key === "proposalIdentification2") {
            switch (changeAction.type) {
              case "digitalSignature":
                await digitalSignatureCall();
                break;
              case "digitalSignatureWithPopup":
                bus.$emit('digitalSignatureEvent');
                break;
              case "digitalSignatureBBVAES":
                bus.$emit('digitalSignatureBBVAES');
                break;
              case "credibomDigitalSignatureWithPopup":
                bus.$emit('credibomDigitalSignatureEvent');
                break;
              case "ptDigitalSignature":
                bus.$emit('ptDigitalSignatureEvent');
                break;
              case "showOnlyPopupWithMsg":
                bus.$emit('showOnlyPopupWithMsgEvent');
                break;
              case "bpiProposalDoc":
                bus.$emit('bpiProposalDocEvent');
                break;
              case "digitalSignatureCaixabank":
                bus.$emit('digitalSignatureCaixabank');
                break;
              case "sabadellDigitalSignatureWithPopup":
                bus.$emit('sabadellDigitalSignatureEvent');
                break;
              case "bbvaPTDigitalSignature":
                bus.$emit('bbvaPTDigitalSignatureEvent');
                break;
              case "videoIdentificationBtnWithPopup":
                bus.$emit('bbvaESVideoIdentificationEvent');
                break;
              case "cofidisDigitalSignatureWithPopup":
                bus.$emit('cofidisDigitalSignatureEvent');
                break;
              default:
                break;
            }
          }

          if (changeAction.type === "forfeitProposal" && !forfeitProposalFlag) {
            forfeitProposalFlag = true;
            bus.$emit('forfeitProposal');
          }

          if (changeAction.type === "changeProposalStatusBtn" && !changeProposalStatusFlag) {
            changeProposalStatusFlag = true;//TODO 
            bus.$emit('changeProposalStatusBtn');
          }

          if (changeAction.type === "historyBtn" && !historyBtnFlag) {
            historyBtnFlag = true;
            bus.$emit('historyBtn', state.history);
          }

          if (changeAction.type === "logsBtn" && !logsBtnFlag) {
            logsBtnFlag = true;
            bus.$emit('logsBtn');
          }
          if (changeAction.type === "updateProposalWithCarData" && !updateCarDataFlag) {
            updateCarDataFlag = true;
            bus.$emit('updateProposalCarDataEvent');
          }

          if (changeAction.type === "docIcBtn" && !icDocBtnFlag) {
            icDocBtnFlag = true;
            bus.$emit('docIcBtn');
          }

        });
      } else {
        const field = form.fields.find((f) => f.key === key);
        if (field) {
          field.value = value;
          
          if (field.key === "carChassis") {
            field.value = value?.trim();
          }

          if (field.type === "toggle") {
            field.value = value === "right" ? "N" : "S";
          }

          if (field.type === "dropdown") {
            field.value = value?.text;
          }

          if (field.key === "businessManagerName") {
            field.value = value;
          }

          if (field.type === "bank-info") {
            field.value = value.value;
            state.ibanData = value.value;
            value.id === 1 ? state.ibanNationalData = value : state.ibanInternationalData = value;
          }

          //FIXME: Verificar se é necessário alterar para outra dropdown o set do valor
          if (field.key === "carYear" || field.key === "carMonth") {
            const valueOfValueObj = value;
            field.value = valueOfValueObj.text;
          }

          field.hasError = false;
        }
      }

      if (form?.key?.startsWith("publicPosition")) {
        const politicalPositionDescKeys = {
          "itself": "itselfDescription",
          "family": "familyDescription",
          "relationswith": "relationswithDescription",
          "otherPosition": "otherPositionDescription"
        };

        form?.fields?.forEach((field) => {
          if (field?.type === "neo-toggle") {
            const fieldKey = field?.key;
            const matchingKeys = Object.keys(politicalPositionDescKeys)
              .filter(key => fieldKey && (key === fieldKey || fieldKey.startsWith(key)));

            matchingKeys.forEach((key) => {
              const fieldDescriptionKey = politicalPositionDescKeys[key];
              const fieldDescription = form.fields.find((f) => f?.key?.startsWith(fieldDescriptionKey));

              if (fieldDescription) {
                fieldDescription.required = field?.value === 1;
              }
            });
          }
        });
      }
    });
  },
  setFieldAttribute(state, { key, attribute, value }) {
    state?.activeTab?.form?.forEach((form) => {
      const field = form?.fields?.find((f) => f.key === key);
      if (field) {
        field[attribute] = value;
      }
    });
  },
  setNextActiveTab(state) {
    const activeTab = state.form.tabs.find((tab) => tab.active);
    const activeTabIndex = state.form.tabs.indexOf(activeTab);
    //If activeTabIndex is the index of the last tab in the array, 
    //adding 1 would result in an index that is out of bounds. 
    //To handle this, we use the modulo (%) operator to calculate the remainder of dividing (activeTabIndex + 1) by state.form.tabs.length. 
    //This ensures that we "wrap around" to the beginning of the array if we reach the end.
    const nextTabIndex = (activeTabIndex + 1) % state.form.tabs.length;
    const nextTab = state.form.tabs[nextTabIndex];
    this.commit("FormModule/setActiveTab", nextTab?.id);
  },
  setDisabledActionButtons(state, disabled) {
    state.disabledActionButtons = disabled;
  },
  setLoading(state, loading) {
    state.loading = loading;
  },
  setIncrementField(state, { key }) {
    state?.activeTab?.form?.forEach((form) => {
      const field = form?.fields?.find((f) => f.key === key);
      if (field) {
        field.keyReRender += 1;
      }
    });
  },
  setActiveBankInfo(state, bankTypeOptionKey) {
    state.bankInfoType = bankTypeOptionKey;
  },
  setFormNationality(state, formNationality) {
    state.formNationality = formNationality;
  },
  setParticipantOptions(state, participantOptions) {
    state.participantOptions = participantOptions;
  },
  setCopyAddressObject(state, copyAddressObject) {
    state.copyAddressObject = copyAddressObject;
  },
  setHistory(state, history) {
    state.history = history;
  }
};

const actions = {
  async checkIfFormIsValid({ commit, rootGetters }, activeOnly = true) {
    Vue.$toast.clear();
    commit("setTableLoading", false);
    const fieldsWithError = new Map();
    const rgpdsFieldNotAccepted = new Set();
    let uniqueRgpdsFieldNotAccepted = [];
    let outOfContextErrors = new Map();
    const formNationality = state.formNationality;
    const bankInfo = state.bankInfoType;
    let hasErrorOnIban = false;
    let hasMonthYearError = false;
    const finId = rootGetters["ProposalsModule/getProposalFinId"];
    const getDocumentsList = rootGetters["ProposalsModule/getDocumentsList"];
    const proposalStatus = rootGetters["ProposalsModule/getProposalStatus"];

    let tabs = state.form.tabs;
    //let fullName = '';

    if (finId === FINANCIAL_IDS.BBVA && proposalStatus === 50){
      let carDataForm = tabs.find((tab) => tab.key === "carData")?.form[0];
      const carChassis = carDataForm?.fields?.find((field) => field.key === 'carChassis');
      const carPlate = carDataForm?.fields?.find((field) => field.key === 'carPlate');
      const carDtPlate = carDataForm?.fields?.find((field) => field.key === 'carDtplate');
      if(carChassis?.value === "" && carPlate?.value === ""){
        Vue.$toast.open({
          message: `<li>${i18n.t("form.error.oneOfRequiredFieldWithNoValue", { fieldOne: i18n.t(carChassis?.label), fieldTwo: i18n.t(carPlate?.label) })}</li>`,
          dismissible: true,
          type: "error",
        });
        carChassis.hasError = true;
        carPlate.hasError = true;
        return false;
      }
      if (carPlate?.value !== "" && carDtPlate?.value === undefined){
        Vue.$toast.open({
          message: `<li>${i18n.t("form.error.requiredFieldWithNoValue", { field: i18n.t(carDtPlate?.label) })}</li>`,
          dismissible: true,
          type: "error",
        });
        carDtPlate.hasError = true;
        return false;
      }
    }

    if (activeOnly) {
      // filter for active tabs only
      tabs = tabs.filter((tab) => tab.active);
    }

    tabs.forEach((formx) => formx.form.forEach((form) => {
      if (!outOfContextErrors.has(i18n.t(formx.title))) {
        outOfContextErrors.set(i18n.t(formx.title), [])
      }
      if (!fieldsWithError.has(i18n.t(formx.title))) {
        fieldsWithError.set(i18n.t(formx.title), [])
      }
      //Validação do tab das viaturas
      const carType = form?.fields?.find((field) => field.key === 'carType');
      const currentDate = new Date();
      const carDtplate = form?.fields?.find((field) => field.key === 'carDtplate')?.value;

      if (carType?.value === "Novo") {
        if (carDtplate) {
          const carDt = new Date(carDtplate);
          // Encontrar os veiculos com data de matricula inferior a 3 meses - data corrente
          const differenceInMs = currentDate - carDt;
          const differenceInDays = Math.floor(differenceInMs / (1000 * 60 * 60 * 24));

          if (carDt.getFullYear() !== currentDate.getFullYear()) {
            console.log('year: ', currentDate.getFullYear())
            Vue.$toast.open({
              message: i18n.t("form.error.belowCurrentPlateDate").replace('{YEAR}', currentDate.getFullYear()  ),
              dismissible: true,
              type: "error",
            });
            form.fields.find((field) => field.key === 'carDtplate').hasError = true;
            hasMonthYearError = true;
          } else if (carDt > currentDate && differenceInDays < 90) {
            form.fields.find((field) => field.key === 'carPlate').required = false;
          } else {
            form.fields.find((field) => field.key === 'carDtplate').hasError = false;
          }
        }
      } else {
        // if (carDtplate) {
        //   const carDt = new Date(carDtplate);
        //   const threeMonthsInMs = 90 * 24 * 60 * 60 * 1000; // 90 days in milliseconds
        //   const threeMonthsFromNow = new Date(currentDate.getTime() + threeMonthsInMs);
        //   if (carDt >= currentDate && carDt <= threeMonthsFromNow) {
        //     form.fields.find((field) => field.key === 'carPlate').required = false;
        //     form.fields.find((field) => field.key === 'carPlate').hasError = false;
        //   } else {
        //     form.fields.find((field) => field.key === 'carPlate').required = true;
        //   }
        // }
      }

      form.fields.forEach((field) => {
        if (field?.type === "date" && field.hasErrorInValue) {
          let errorMessage = i18n.t("form.error.invalidDate", { field: i18n.t(field?.label) });
          outOfContextErrors.get(i18n.t(formx.title)).push(errorMessage);
        }

        // validação do campo kms
        if (field.key === 'carKms' && field.value.length > 9) {
          Vue.$toast.open({
            message: i18n.t("form.error.aboveLimitsKms"),
            dismissible: true,
            type: "error",
          });
          field.hasError = true;
          return false;
        }

        //campo bastidor - só é válido se tiver 17 caracteres
        if (field.key === 'carChassis') {
          if (field.value != '' && field.value?.trim().length != 17) {
            let errorMessage = i18n.t("form.error.invalidChassis");
            outOfContextErrors.get(i18n.t(formx.title)).push(errorMessage);
            field.hasError = true;
            return false;
          }
        }

        // validação do campo IBAN
        if (field.key === 'NIB') {
          const ibanValue = field.value;
          let errorMessage = "";
          let isValid = false;

          if (formNationality === "PT" && bankInfo?.value === 1) {
            isValid = iban.isValid(`PT50${ibanValue}`);
            errorMessage = i18n.t("form.error.IBANNationalerror");
          } else if (formNationality === "ES" && bankInfo?.value === 1) {
            isValid = iban.isValid(`ES${ibanValue}`);
            errorMessage = i18n.t("form.error.IBANNationalerror");
          } else {
            isValid = ibanValue?.length === 34;
            errorMessage = i18n.t("form.error.IBANInternationalerror");
          }
          if (!isValid) {
            const validation = hasInvalidValue(field);
            field.hasError = true;
            Vue.$toast.open({
              message: errorMessage,
              dismissible: true,
              type: "error",
            });
            if (!validation) hasErrorOnIban = true;
          }
        }

        // se o field estiver activo e for required, validar valor
        // se o field tiver visivel
        if (!field.disabled && !field.hidden && field.required && field.editable && field.fieldShow && !hasMonthYearError && !hasErrorOnIban) {
          const validation = hasInvalidValue(field);
          field.hasError = validation;
          if (validation) fieldsWithError.get(i18n.t(formx.title)).push(field.label);
        }
        // Porque quando o tipo de documento é BI/Cartão de Cidadão o field tá disabled e não entra no if de cima
        else if (field.key === "documentType" && field.fieldShow && !field.disabled && !field.hidden) {
          const validation = hasInvalidValue(field);
          field.hasError = validation;
          if (validation) fieldsWithError.get(i18n.t(formx.title)).push(field.label);
        }

        // validação do campo BI
        if (field?.key?.startsWith('BI') && formNationality === "PT") {
          const biValue = field.value;
          let errorMessage = "";
          if (biValue && field?.fieldShow) {
            let isValid = validateBICC(biValue);
            let isValidBI = validateBI(biValue, biValue.at(-1));
            if (!(isValid || isValidBI)) {
              errorMessage = i18n.t("form.error.biPTFormat");
              outOfContextErrors.get(i18n.t(formx.title)).push(errorMessage);
              field.hasError = true;
            }
          }

          if (biValue && !field?.fieldShow) {
            field.value = '';
          }
        }

        if (field?.key?.startsWith('BI') && formNationality === "ES") {
          const biValue = field.value;
          let errorMessage = "";
          if (biValue) {
            const nationality = state.activeTab.form.find((form) => form?.key?.startsWith("personalData"))?.fields.find((field) => field?.key?.startsWith("nationality"))?.value;
            const isSpanish = isSpanishNational(nationality);
            console.log('nationality: ', nationality, isSpanish)
            let isValid = validateDNI(biValue, isSpanish);
            if (!isValid) {
              errorMessage = i18n.t("form.error.biPTFormat");
              outOfContextErrors.get(i18n.t(formx.title)).push(errorMessage);
              field.hasError = true;
            }
          }
        }

        // https://dtogsc.atlassian.net/browse/SIFA-2123
        // Melhoria: Santander - Se o titular for empresa, só é possível seleccionar o IBAN do primeiro interveniente
        if (field.key === 'nibHolder') {
          const nibHolder = field.value;
          let errorMessage = "";
          const isACompanyFirstParticipant = store.getters["ProposalsModule/getIsCompanyClientType"];
          // IF
          // nibHolder exists
          // finID ---> Santander
          // It's a company
          // Nib holder ---> Segundo Interveniente
          if (nibHolder && finId === 4 && isACompanyFirstParticipant && nibHolder === "Segundo Interveniente") {
            errorMessage = i18n.t("form.error.ibanCompanyHolder");
            outOfContextErrors.get(i18n.t(formx.title)).push(errorMessage);
            field.hasError = true;
          }
        }

        // validação do campo birthdate
        if (field?.key?.startsWith('birthdate')) {
          const birthdateValue = field.value;
          let errorMessage = "";
          const allTabs = state.form.tabs;
          allTabs.forEach((formx) => formx.form.forEach((form) => {
            const ppcName = form.fields.find((field) => field.key.startsWith("insurancePPCName"))?.value;
            if (ppcName === 'PPP' && birthdateValue && finId === 12) {
              const today = new Date();
              const birthdate = new Date(birthdateValue);
              const age = today.getFullYear() - birthdate.getFullYear();
              const insurancePPCNrInsuredPeople = form.fields.find((field) => field.key.startsWith("insurancePPCNrInsuredPeople"))?.value;

              if (age > 65 && ((field?.key === "birthdateText" && insurancePPCNrInsuredPeople === 1) || (field?.key === "birthdateText_1" && insurancePPCNrInsuredPeople === 2))) {
                errorMessage = i18n.t("form.error.PPPAgeValidation");
                outOfContextErrors.get(i18n.t(formx.title)).push(errorMessage);
                field.hasError = true;
              }
            } else if (ppcName === 'Vida Senior' && birthdateValue && finId === 9) {
              const today = new Date();
              const birthdate = new Date(birthdateValue);
              const insurancePPCNrInsuredPeople = form.fields.find((field) => field.key.startsWith("insurancePPCNrInsuredPeople"))?.value;
              const age = today.getFullYear() - birthdate.getFullYear();

              if ((age < 65 || age > 75) && field?.key === "birthdateText" && insurancePPCNrInsuredPeople === 1) {
                errorMessage = i18n.t("form.error.seniorAgeValidation");
                outOfContextErrors.get(i18n.t(formx.title)).push(errorMessage);
                field.hasError = true;
              }
            }
          }));
        }

         // validação do campo (A diferença entre o fecho de antiguidade e a idade não deve ser inferior a 18 anos)
        if (field?.key?.startsWith('DTHiredText')) {
          const dtHiredValue = field?.value;
          let errorMessage = "";

          const allTabs = state.form.tabs;
          allTabs.forEach((formx) => formx.form.forEach((form) => {
            const birthdateField = form.fields.find((field) => field?.key?.startsWith("birthdate"))
            const birthdateValue = birthdateField?.value;
            if (dtHiredValue && birthdateValue && finId === FINANCIAL_IDS.CETELEM_ES) {
                const birthdate = new Date(birthdateValue);
                const dtHired = new Date(dtHiredValue);
                const age = dtHired.getFullYear() - birthdate.getFullYear();
                if (age < 18 ) {
                  errorMessage = i18n.t("form.error.dtHiredWithbirthdateValidation");
                  outOfContextErrors.get(i18n.t(formx.title)).push(errorMessage);
                  field.hasError = true;
                }
            }
          }));
        }

        // validação do campo EMAIL
        if (field?.key?.startsWith('email')) {
          const emailValue = field.value;
          let errorMessage = "";
          if (emailValue) {
            let isNotValid = testIfItIsNotEmail(emailValue);
            if (isNotValid) {
              errorMessage = i18n.t("form.error.emailFormat");
              outOfContextErrors.get(i18n.t(formx.title)).push(errorMessage);
              field.hasError = true;
            }
          }
        }

        // validação do campo NIPC
        if (field?.key?.startsWith('NIPC')) {
          const nipc = field.value;
          let errorMessage = "";

          if (nipc && nipc.length !== 9) {
            errorMessage = i18n.t("form.error.nipcFormat");
            outOfContextErrors.get(i18n.t(formx.title)).push(errorMessage);
            field.hasError = true;
          }
        }

        // validação do campo NRCRC
        if (field?.key?.startsWith('NRCRC')) {
          const nccrc = field.value;
          let errorMessage = "";
          if (nccrc) {
            let isNotValid = testIfItIsNotValidNrCRC(nccrc);
            if (isNotValid) {
              errorMessage = i18n.t("form.error.nrCRCFormat");
              outOfContextErrors.get(i18n.t(formx.title)).push(errorMessage);
              field.hasError = true;
            }
          }
        }

        // validação Full name
        if (field?.key?.startsWith('firstName')) {
          const firstName = field.value;
          let errorMessage = "";

          let fullName = firstName;
          form.fields.forEach((field) => {
            if (field?.key?.startsWith('firstName')) {
              const firstSurname = form.fields.find((field) => field.key.startsWith("firstSurname"))?.value;
              fullName += firstSurname;
              console.log("firstSurname: " + firstSurname);
            }
          });

          form.fields.forEach((field) => {
            if (field?.key?.startsWith('firstName')) {
              const secondSurname = form.fields.find((field) => field.key.startsWith("secondSurname"))?.value;
              console.log("secondSurname: " + secondSurname);
              fullName += secondSurname;
            }
          });

          const fullNameLength = fullName.length;
          if (fullNameLength < 5 || fullNameLength > 97) {
            errorMessage = i18n.t("form.error.fullNameValidationLength");
            outOfContextErrors.get(i18n.t(formx.title)).push(errorMessage);
            field.hasError = true;
          }
        }

        if (field?.key?.startsWith('firstName')) {
          const firstName = field.value;
          let errorMessage = "";
          if (firstName) {
            const isNotValid = isNotValidLetters(firstName);
            if (isNotValid) {
              errorMessage = i18n.t("form.error.namesValidation");
              outOfContextErrors.get(i18n.t(formx.title)).push(errorMessage);
              field.hasError = true;
            }
          }
        }

        if (field?.key?.startsWith('secondSurname')) {
          const secondSurname = field.value;
          let errorMessage = "";
          if (secondSurname) {
            const isNotValid = isNotValidLetters(secondSurname);
            if (isNotValid) {
              errorMessage = i18n.t("form.error.namesValidation");
              outOfContextErrors.get(i18n.t(formx.title)).push(errorMessage);
              field.hasError = true;
            }
          }
        }

        if (field?.key?.startsWith('firstSurname')) {
          const firstSurname = field.value;
          let errorMessage = "";
          if (firstSurname) {
            const isNotValid = isNotValidLetters(firstSurname);
            if (isNotValid) {
              errorMessage = i18n.t("form.error.namesValidation");
              outOfContextErrors.get(i18n.t(formx.title)).push(errorMessage);
              field.hasError = true;
            }
          }
        }

        // validação do campo NIF
        if (field?.key?.startsWith('NIF')) {
          const nifValue = field.value;
          let errorMessage = "";
          if (nifValue && nifValue.length < 9) {
            errorMessage = i18n.t("form.error.nifFormat");
            outOfContextErrors.get(i18n.t(formx.title)).push(errorMessage);
            field.hasError = true;
          }
        }

        // validação do campo company Phone
        if (field?.key?.startsWith('companyPhone')) {
          const companyPhoneValue = field.value;
          let errorMessage = "";
          if (companyPhoneValue && companyPhoneValue.length < 9) {
            errorMessage = i18n.t("form.error.companyPhoneFormat");
            outOfContextErrors.get(i18n.t(formx.title)).push(errorMessage);
            field.hasError = true;
          }
        }

        // validação do campo Dependentes
        if (field?.key?.startsWith('nrDependents')) {
          const nrDependentsValue = field.value;
          let errorMessage = "";
          const proposalFinId = store.getters["ProposalsModule/getProposalFinId"];
          //Cetelem Spain - 9
          const maxNrDependents = proposalFinId == 16 ? 9 : 10;
          if (nrDependentsValue && parseInt(nrDependentsValue) > maxNrDependents) {
            errorMessage = i18n.t("form.error.nrDependentsValidation") + maxNrDependents + '.';
            outOfContextErrors.get(i18n.t(formx.title)).push(errorMessage);
            field.hasError = true;
          }
        }

        // validação rendimentos quando a profissão não é remunerada ou não tem profissão
        const otherProfits = formx?.form?.find((form) => form?.key?.startsWith('profit'))?.fields?.find((field) => field?.key?.startsWith('otherProfits'));
        const otherProfitsOrigin = formx?.form?.find((form) => form?.key?.startsWith('profit'))?.fields?.find((field) => field?.key.startsWith('otherProfitsOrigin'));

        if (form?.key?.startsWith("professionalStatus") && field?.key?.startsWith("professionalCategory") && (field.value === 'Ama de casa' || field.value === 'Desempleado sin prestación' || field.value === 'Estudiante')) {
          if (otherProfits?.value || otherProfitsOrigin?.value) {
            Vue.$toast.open({
              message: i18n.t("form.error.jobAlertNoProfit"),
              dismissible: true,
              type: "error",
            });
            otherProfits.hasError = !!otherProfits?.value;
            otherProfitsOrigin.hasError = !!otherProfitsOrigin?.value;
            return false;
          }
        } else if (otherProfits && otherProfitsOrigin) {
          otherProfits.hasError = false;
          otherProfitsOrigin.hasError = false;
        }

        //se for uma particular não deve conseguir avançar com uma categoria profissional de Autónomo ou profesional liberal/freelance
        if (form?.key?.startsWith("professionalStatus")) {
          const idClienttype = formx?.form?.find((form) => form?.key?.startsWith('generalData'))?.fields?.find((field) => field?.key?.startsWith('idClienttype'));
          const professionalCategory = formx?.form?.find((form) => form?.key?.startsWith('professionalStatus'))?.fields?.find((field) => field?.key?.startsWith('professionalCategory'));
          const professionalStatus = formx?.form?.find((form) => form?.key?.startsWith('professionalStatus'))?.fields?.find((field) => field?.key?.startsWith('professionalStatus'));
          const professionalCategoryValue = professionalCategory?.value;
          const professionalStatusValue = professionalStatus?.value;
          const participantType = idClienttype.value;

          const isFieldKeyProfessionalCategory = field?.key?.startsWith('professionalCategory');

          let errorMessage = "";
          const proposalFinId = store.getters["ProposalsModule/getProposalFinId"];
          if ((proposalFinId === 12 || proposalFinId === 13) && participantType === 1 && (professionalCategoryValue === 'Autónomo') && isFieldKeyProfessionalCategory) {
            errorMessage = i18n.t("form.error.professionalCategoryIndependent");
            outOfContextErrors.get(i18n.t(formx.title)).push(errorMessage);
            professionalCategory.hasError = true;
          }

          // parece ser a mesma validação que a de cima
          /*if ((proposalFinId === 12 || proposalFinId === 13) && participantType === 1 && (professionalCategoryValue === 'Autónomo') && isFieldKeyProfessionalCategory) {
            errorMessage = i18n.t("form.error.professionalCategoryIndependent");
            outOfContextErrors.get(i18n.t(formx.title)).push(errorMessage);
            professionalCategory.hasError = true;
          }*/

          if ((proposalFinId === 12 || proposalFinId === 13) && participantType === 1 && (professionalStatusValue === 'Autónomo')) {
            errorMessage = i18n.t("form.error.professionalStatusIndependent");
            outOfContextErrors.get(i18n.t(formx.title)).push(errorMessage);
            professionalStatus.hasError = true;
          }

          if (proposalFinId === 13 && participantType === 1 && professionalStatusValue === 'Profesión liberal/Freelance') {
            errorMessage = i18n.t("form.error.professionalStatusIndependent");
            outOfContextErrors.get(i18n.t(formx.title)).push(errorMessage);
            professionalStatus.hasError = true;
          }

        }

        //const companySectorObj = formx?.form?.find((form) => form?.key?.startsWith('professionalStatus'))?.fields?.find((field) => field?.key.startsWith('companyActivity'));
        
        /*
        *SIFA-2581 - Sofinco - Alterar a forma como mostramos o sector de actividade
        if (form?.key?.startsWith("professionalStatus") && field?.key?.startsWith("companyActivity") && finId === 13) {
          const companySector = field.value;
          let errorMessage = "";
          const desiredElements = ['Administración pública', 'Serv financieros y seguros', 'Consultoria', 'Enseñanza', 'Salud', 'Servicios profesionales', 'Comunicacion, prensa, radio y televisión', 'Cultura y deportes', 'Comercio', 'Industria y energía', 'Transporte', 'Inmobiliaria y construcción', 'Hosteleria y restauración', 'Agricultura, ganadería y pesca'];

          form.fields.forEach((field1) => {
            if (field1?.key?.startsWith('professionalCategory') && (field1.value === 'Autónomo' || field1.value === 'Profesión liberal/Freelance')) {
              if (desiredElements?.includes(companySector)) {
                errorMessage = i18n.t("form.error.companyActivityIndependent");
                outOfContextErrors.get(i18n.t(formx.title)).push(errorMessage);
                field.hasError = true;
              }
            }
          });
        }
        */

        // validações RGPD
        formx?.form?.find((form) => form?.key?.startsWith('rgpd'))?.fields?.map((field) => {
          const proposalFinId = store.getters["ProposalsModule/getProposalFinId"];
          // Validação da primeira opção do RGPD apenas para a sofinco e BBVA ES
          switch (proposalFinId) {
            //cetelem spain
            case FINANCIAL_IDS.CETELEM_ES:
              /*SIFA-2800 - RGPD - Retirar a frase e enviar sempre a Accept
              if (field?.key?.startsWith("commercialAndMarketingDataUsage") && field.value === 2
              ) {
                rgpdsFieldNotAccepted.add(field.errorToast);
                field.hasError = true;
              }
              /*if (field?.key?.startsWith("GDPRPersonalDataProtection") && field.value === 2
              ) {
                rgpdsFieldNotAccepted.add(field.errorToast);
                field.hasError = true;
              }*/
              break;
            // Sofinco
            case FINANCIAL_IDS.SOFINCO:
              if (field?.key?.startsWith("commercialAndMarketingDataUsage") && field.value === 2) {
                rgpdsFieldNotAccepted.add(field.errorToast);
              }
              break;
            // BBVA ES
            case 12:
            //  if (field?.key?.startsWith("commercialAndMarketingDataUsage") && field.value === 2) {
            //   rgpdsFieldNotAccepted.add(field.errorToast);
            //  }
              if (field?.key?.startsWith("contractManagementDataUsage") && field.value === 2) {
                rgpdsFieldNotAccepted.add(field.errorToast);
              }
            break;
            // Credibom
            // https://dtogsc.atlassian.net/browse/SIFA-1566
            // Remover a obrigatoriedade de ter 1 campo de RGPD a sim
            // case 9:
            //   if (field?.key?.startsWith("commercialAndMarketingDataUsage") && field.value === 2) {
            //     rgpdsFieldNotAccepted.add(field.errorToast);
            //   }
            //   break;
            case FINANCIAL_IDS.CAIXABANK:
              if (field?.key?.startsWith("commercialAndMarketingDataUsage") && field.value === 2) {
                rgpdsFieldNotAccepted.add(field.errorToast);
              }
              if (field?.key?.startsWith("contractManagementDataUsage") && field.value === 2) {
                rgpdsFieldNotAccepted.add(field.errorToast);
              }
              if (field?.key?.startsWith("GDPRSendInfoProducts") && field.value === 2) {
                rgpdsFieldNotAccepted.add(field.errorToast);
              }
              if (field?.key?.startsWith("clientSatisfactionDataUsage") && field.value === 2) {
                rgpdsFieldNotAccepted.add(field.errorToast);
              }
              break;
            default:
          }

        });

        uniqueRgpdsFieldNotAccepted = [...rgpdsFieldNotAccepted];

        if (field.key.startsWith("quota") && field?.required) {
          let quotaAggregator = 0;
          const allTabs = state.form.tabs;
          let outOfTheQuestion = false;

          if (field.key.startsWith("quota") && !isNaN(field?.value)) {
            if (Number(field?.value) === 0 && !outOfTheQuestion) {
              const errorMessage = i18n.t("form.error.quotaValidation");
              outOfContextErrors.get(i18n.t(formx.title)).push(errorMessage);
              field.hasError = true;
              outOfTheQuestion = true;
              return;
            }
          }

          if (!outOfTheQuestion) {
            allTabs.forEach((formx) => formx.form.forEach((form) => {
              form.fields.forEach((field) => {
                if (field.key.startsWith("quota") && !isNaN(field?.value)) {
                  quotaAggregator += Number(field?.value);
                }
              });
            }));

            if (quotaAggregator > 100 && !outOfTheQuestion) {
              const errorMessage = i18n.t("form.error.quotaValidation");
              outOfContextErrors.get(i18n.t(formx.title)).push(errorMessage);
              field.hasError = true;
            }
          }
        }

        if (field.key.startsWith("relationToProponent") && field?.required) {
          if (field?.value === "Selecione uma opção" || !isNaN(field?.value)) {
            fieldsWithError.get(i18n.t(formx.title)).push(field.label);
            field.hasError = true;
          }
        }

        if (field.key.startsWith("companyActivity") && field?.required) {
          if (field?.value === i18n.t("dropdownSelection") || field?.value === "Seleccione una opción") {
            fieldsWithError.get(i18n.t(formx.title)).push(field.label);
            field.hasError = true;
          }
        }

        // validação do phone1 no tab do titular 1
        const isACompanyFirstParticipant = store.getters["ProposalsModule/getIsCompanyClientType"];

        if (!isACompanyFirstParticipant) {
          //Validação dos nomes
          if (form?.key?.startsWith("generalData") && field.key.startsWith("firstName")) {
            const participantTabs = state.form.tabs.filter((tab) => tab?.dynamicallyCreated || tab?.id === 4);

            // const firstName = form?.fields.find((field) => field?.key.startsWith(("firstName")));
            const firstSurname = form?.fields.find((field) => field?.key.startsWith(("firstSurname")));
            const secondSurname = form?.fields.find((field) => field?.key.startsWith(("secondSurname")));

            if (participantTabs.length > 1) {
              const participantTabsGeneralDataX = participantTabs.map((tab) => {
                const generalData = tab?.form.filter((formFields) => formFields?.key?.startsWith("generalData"));
                const generalDataFields = generalData.flatMap((fields) => fields?.fields);
                const filteredGeneralDataFields = generalDataFields.filter((filteredFields) => filteredFields?.key.startsWith("firstName")
                  || filteredFields?.key.startsWith("firstSurname") || filteredFields?.key.startsWith("secondSurname"));
                return {
                  id: tab?.id,
                  section: filteredGeneralDataFields
                }
              })

              participantTabsGeneralDataX.forEach((generalData) => {
                const filteredArr = generalData.section.filter((thisField) => thisField.value && ((thisField.key.startsWith("firstName") && thisField.value === field?.value) ||
                  (thisField.key.startsWith("firstSurname") && thisField.value === firstSurname?.value) ||
                  (thisField.key.startsWith("secondSurname") && thisField.value === secondSurname?.value)));
                const hasThisKey = filteredArr.some((f) => f?.key === field.key);

                if (!hasThisKey && filteredArr.length === 3) {
                  filteredArr.map((x) => { x.hasError = true; return x; })
                  const errorMessage = i18n.t("form.error.nameValidation");
                  outOfContextErrors.get(i18n.t(formx.title)).push(errorMessage);
                }
              });
            }
          }
          // validação de email e contacto
          else if (form?.key?.startsWith("personalData") || form?.key?.startsWith("contactData")) {
            const keysToIterate = ["NIF", "BI", "NISS", "phone1", "email"];
            const keysToIterateLabels = {
              "NIF": "form.error.nifValidation", "BI": "form.error.biValidation",
              "NISS": "form.error.nissValidation", "phone1": "form.error.phoneValidation", "email": "form.error.emailValidation"
            };
            throwLabelVal(keysToIterate, field, keysToIterateLabels, outOfContextErrors);
          }
        }

        //outOfContextErrors = Array.from(new Set(outOfContextErrors));

      });

      const nrSalaryPayments = form?.fields?.find((field) => field?.key?.startsWith('nrSalaryPayments'));
      const netReceiptValueAvg = form?.fields?.find((field) => field?.key?.startsWith('netReceiptValueAvg'));

      if (nrSalaryPayments && netReceiptValueAvg) {
        nrSalaryPayments.value = !nrSalaryPayments?.value || nrSalaryPayments?.value === "0" || nrSalaryPayments?.value === 0 ? 0 : nrSalaryPayments?.value;
        netReceiptValueAvg.value = !netReceiptValueAvg?.value || netReceiptValueAvg?.value === "0" || netReceiptValueAvg?.value === 0 ? 0 : netReceiptValueAvg?.value;
        if ((!netReceiptValueAvg?.value || netReceiptValueAvg?.value === 0) && (nrSalaryPayments?.value !== 0)) {
          nrSalaryPayments.hasError = true;
          outOfContextErrors.get(i18n.t(formx.title)).push("form.error.nrPaymentsAndSalary");
        } else if (netReceiptValueAvg.value && nrSalaryPayments?.value === 0) {
          nrSalaryPayments.hasError = true;
          outOfContextErrors.get(i18n.t(formx.title)).push("form.error.nrPaymentsAndSalaryAbove");
        }
      }
    }));

    //BPI
    //Folha proposta Validar + Seguinte para o ecrã: Ações PT 
    // História: Como Gestor de negócio, quero que os documentos da proposta sejam validados, por forma a ser possível enviar a proposta para a financeira
    // #1 Cenário: Validar que o tipo de documento: Folha de Proposta Assinada se encontra adicionado na proposta
    // Dada uma proposta da Financeira: BPI
    // E em que não foi feito o upload da folha de proposta
    // Quando o gestor de negócio tenta enviar a proposta para a Financeira 
    // Então verifica que a proposta não é enviada 
    // E que surge uma mensagem de erro no canto superior direito a vermelho
    //https://dtogsc.atlassian.net/browse/SIFA-330
    if (finId === 6 && !activeOnly) {
      const hasBPIDocSigned = getDocumentsList.some((doc) => doc?.type === "Folha de proposta assinada" && doc?.status?.existsStatus)
      if (!hasBPIDocSigned) {
        if (outOfContextErrors.get(i18n.t("pages.ProposalPage.proposalsForm.action.codes.files.title")) !== undefined)
          outOfContextErrors.get(i18n.t("pages.ProposalPage.proposalsForm.action.codes.files.title")).push("form.error.bpiDocRequired");
      }
    }

    let errorMessage = "<ul>";
    if (hasErrorOnIban || hasMonthYearError) return false;

    let fieldWithErrorValidation, outOfContextErrorsValidation = false;
    fieldsWithError.forEach((value, key) => {
      if (fieldsWithError.get(key).length){
        fieldWithErrorValidation = true;
      }
    })

    outOfContextErrors.forEach((value, key) => {
      if (outOfContextErrors.get(key).length) {
        outOfContextErrorsValidation = true;
      }
    })

    if (fieldWithErrorValidation || uniqueRgpdsFieldNotAccepted.length || outOfContextErrorsValidation) {

      fieldsWithError.forEach((value, key) => {
        if (fieldsWithError.get(key).length){
          fieldsWithError.get(key).forEach((field) => {
            errorMessage += `<li><b>${key}</b> ${i18n.t("form.error.requiredFieldWithNoValue", { field: i18n.t(field) })}</li>`;
          })
        }
      });

      uniqueRgpdsFieldNotAccepted.forEach((label) => {
        errorMessage += `<li>${i18n.t(label)}</li>`;
      });

      outOfContextErrors.forEach((value, key) => {
        if (outOfContextErrors.get(key).length){
          outOfContextErrors.get(key).forEach((field) => {
            errorMessage += `<li><b>${key}</b> ${i18n.t(field)}</li>`;
          })
        }
      });

      errorMessage += "</ul>";

      Vue.$toast.open({
        message: errorMessage,
        dismissible: true,
        type: "error",
      });

      return false;
    } else {
      Vue.$toast.open({
        message: i18n.t("form.success.validation"),
        dismissible: true,
        type: "success",
      });
      return true;
    }
  },
  async checkIfAllFormIsValid({ commit, dispatch }) {
    Vue.$toast.clear();
    commit("setTableLoading", false);

    // Call the checkIfFormIsValid action method
    return await dispatch('checkIfFormIsValid', false);
  },
  async addDynamicTab({ commit }, { formNationality, finId, clientType, interventionType }) {
    commit("setTableLoading", false);
    try {
      const isACompanyFirstParticipant = clientType.clientTypeId === 3;
      const isItACompanyForm = interventionType === 3;
      const isENIType = interventionType === 2;
      const tabsLength = state.form.tabs.length;
      const tabFiveIndex = state.form.tabs.findIndex(item => item.id === 5);
      delete require.cache[require.resolve("../../forms/proposalTabs/tab-4.json")];
      delete require.cache[require.resolve(`../../forms/proposalTabs/tab-4-company.json`)];
      const tabFour = isItACompanyForm ? require(`../../forms/proposalTabs/tab-4-company.json`) : require("../../forms/proposalTabs/tab-4.json");
      const cleanParticipantTabForm = JSON.parse(JSON.stringify(tabFour));

      const tabNumber = tabsLength - 5;

      if (isACompanyFirstParticipant && !isItACompanyForm) {
        companyPartnerField(cleanParticipantTabForm);
      }

      cleanParticipantTabForm.form = filterOutNotUsedFieldsByNationality(cleanParticipantTabForm.form, formNationality);

      cleanParticipantTabForm.btns.unshift({
        "key": "deleteTab",
        "bgColor": "#C2C2C2",
        "label": i18n.t(
          'pages.ProposalsPage.proposalsForm.addNewParticipants.deleteBtnLabel'
        ),
        "icon": "",
        "iconPosition": "",
        "class": "btn-3",
      });

      cleanParticipantTabForm.id = 4 + tabNumber / 10;
      cleanParticipantTabForm.key = (tabNumber).toString();
      cleanParticipantTabForm.title =  + (1 + tabNumber).toString() + "º Titular";
      cleanParticipantTabForm.saveKey = isItACompanyForm ? companyPersonTabMapping[tabNumber] : particularPersonTabMapping[tabNumber];
      cleanParticipantTabForm.parentSaveKey = "clientInfo";
      cleanParticipantTabForm.dynamicallyCreated = true;

      const participantRelationshipField = {
        "label": "pages.ProposalsPage.proposalsForm.participantsData.intervencion.label.relationship",
        "type": "dropdown",
        "key": "relationToProponent",
        "saveKey": "relationToProponent",
        "value": "",
        "editable": true,
        "required": true,
        "flexdir": "col",
        "colspan": 3,
        "fieldShow": false,
        "searchable": true,
        "proposalType": [
          "PT",
          "ES"
        ],
        "populateOptionsActions": [
          {
            "type": "action",
            "name": "ProposalsModule/getRelationshipList",
            "payload": "finId",
            "withKey": true
          },
          {
            "type": "getter",
            "name": "ProposalsModule/getRelationshipList",
            "withKey": true
          }
        ],
        "onChangeActions": [
          {
            "type": "onChangeRelationToProponent",
            "fields": [
            ]
          },
        ],
      };

      if (isACompanyFirstParticipant && finId === FINANCIAL_IDS.BBVA) {
        participantRelationshipField.options = [
          {
            value: -1,
            text: i18n.t("dropdownSelection"),
            disabled: true,
          },
          {
            value: 0,
            text: "Beneficiário",
          },
          {
            value: 1,
            text: "Representante",
          }
        ];

        delete participantRelationshipField.populateOptionsActions;
      }

      // Adicionar opções ao participant no nibHolder dropdown
      const participantOptions = store.getters["FormModule/getParticipantOptions"];
      const participantPosition = tabNumber + 1;
      participantOptions.push({
        value: participantPosition,
        text: i18n.t("pages.ProposalsPage.proposalsForm.bankingInfo.bankData.nibHolder."+nibHolderParticipantMap[participantPosition])
      });
      commit("FormModule/setParticipantOptions", participantOptions);

      cleanParticipantTabForm.form[0].fields.splice(isItACompanyForm ? 2 : 1, 0, participantRelationshipField);

      //FIXME - verificar a necessidade de ter em duplicado - 
      // As keys alteradas (com sufixo) não são adicionadas ao cleanParticipantTabForm.form
      cleanParticipantTabForm.form = concatKeyValues(cleanParticipantTabForm.form, tabNumber);
      cleanParticipantTabForm.form = cleanParticipantTabForm.form.map((form) => formMapperHandler(form, finId, null, process.env.VUE_APP_PROPOSAL_FORM, cleanParticipantTabForm.key, formNationality, clientType, isENIType, cleanParticipantTabForm, true, interventionType));

      //Temporary solution
      await new Promise((resolve) => {
        setTimeout(() => {
          // cleanParticipantTabForm.form = concatKeyValues(cleanParticipantTabForm.form, tabNumber);
          cleanParticipantTabForm.form = cleanParticipantTabForm.form.map((form) => formMapperHandler(form, finId, null, process.env.VUE_APP_PROPOSAL_FORM, cleanParticipantTabForm.key, formNationality, clientType, isENIType, cleanParticipantTabForm, true, interventionType));
          state.form.tabs.splice(tabFiveIndex, 0, cleanParticipantTabForm);
          resolve();
        }, 3000);
      });
    } catch (error) {
      Vue.$toast.open({
        message: error.message,
        dismissible: true,
        type: "error",
      });
    }
  },
  removeTab({ commit }) {
    // ! Se houver mais do que 3 participantes é necessário alterar a lógica
    let { activeTab } = state;
    const { form } = state;
    const { tabs } = form;

    const activeTabIndex = tabs.findIndex((obj) => obj.id === activeTab.id);
    const dynamicCreatedTabs = tabs.filter((obj) => obj.dynamicallyCreated);

    // Remover opção da dropdown nibHolder
    const participantOptions = store.getters["FormModule/getParticipantOptions"];
    participantOptions.pop();
    commit("FormModule/setParticipantOptions", participantOptions);

    if (activeTabIndex !== -1) {
      // dynamicCreatedTabs com mais de 2 tabs criados dinamicamente
      if (dynamicCreatedTabs.length > 2) {
        switch (activeTab.id) {
          case 4.1:
            manipulateRestOfTabs(tabs, 4.2, "2º Titular", 4.1, "third", "second");
            break;
          case 4.2:
            manipulateRestOfTabs(tabs, 4.3, "3º Titular", 4.2, "fourth", "third");
            break;
          default:
            break;
        }
      }

      // Remover o tab
      tabs.splice(activeTabIndex, 1);

      const dynamicCreatedTabsArr = tabs.filter((obj) => obj.dynamicallyCreated);

      commit("setActiveTab", dynamicCreatedTabsArr.length === 1 ? 4.1 : 4);
    }
  }
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
